import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { StandardModal, Toast, useDashboardTheme } from "src/components";
import { UserKeys } from "src/constants/userDetails";
import { FEATURE_FLAGS, getFeatureFlags } from "src/constants/FeatureFlag";
import GreenCheckIcon from "src/img/new/green-check.svg";
import {
    Surface,
    StickyHeader,
    HeaderBar,
    PageName,
    HeaderTools,
    SearchWrap,
    SearchInput,
    SearchResults,
    SearchResultButton,
    GhostSelect,
    LocationPill,
    PrimaryAction,
    Layout,
    LeftPanel,
    CenterPanel,
    RightPanel,
    PanelHeaderRow,
    PanelHeaderTitle,
    PanelCollapseButton,
    PanelCollapseGlyph,
    CollapsedPanelButton,
    CollapsedPanelLabel,
    SectionLabel,
    SectionHeadingRow,
    SectionHeadingActions,
    SectionEditButton,
    SectionInfoButton,
    SectionInfoPopover,
    FilterRow,
    FilterItem,
    FilterLabel,
    ConnectionCard,
    ConnectionRow,
    ConnectionLabel,
    ConnectionText,
    ConnectionTitle,
    ConnectionMeta,
    ConnectionToggleWrap,
    ColorSwatch,
    Toggle,
    CalendarHeader,
    WeekdaysRow,
    MonthHeading,
    NavArrow,
    TodayButton,
    ViewTabs,
    ViewTab,
    CalendarGridWrap,
    MonthWeekRow,
    MonthWeekCells,
    MonthSpanLayer,
    MonthSpanRow,
    MonthSpanItem,
    MonthSpanLead,
    MonthSpanBullet,
    DayAgenda,
    DayHourLabel,
    DayHourRow,
    WeekGrid,
    WeekHourCorner,
    WeekHeaderCell,
    WeekHeaderTitle,
    WeekHeaderDate,
    WeekHourLabel,
    WeekAllDayLabel,
    WeekAllDayArea,
    WeekAllDayLayer,
    WeekAllDayRow,
    WeekSlot,
    YearGrid,
    YearMonthCard,
    YearMonthHeader,
    YearMonthGrid,
    YearWeekday,
    YearDayCell,
    YearDayDot,
    WeekdayHeader,
    DayCell,
    DayCellItems,
    DayNumber,
    ItemPill,
    ItemBullet,
    MoreText,
    DayCellFooter,
    StaticCellLabel,
    SmallText,
    Badge,
    SourceChipRow,
    SourceChip,
    IconButton,
    CalendarSelectWrap,
    CalendarSelectChevron,
    AnalyticsShell,
    AnalyticsHero,
    AnalyticsHeroGrid,
    AnalyticsGrid,
    AnalyticsCard,
    CardTitleRow,
    MetricValue,
    ChipRow,
    FilterChip,
    MiniStatGrid,
    MiniStat,
    TrendBars,
    MetricBars,
    TrendColumn,
    TrendStack,
    TrendTrack,
    TrendBar,
    ChartCaption,
    TrendBarSegment,
    DonutWrap,
    DonutSvg,
    DonutTrack,
    DonutProgress,
    DonutCenter,
    EmptyStateCard,
    TimezoneCalculatorCard,
    TimezoneAttendeeList,
    TimezoneAttendeeRow,
    TimezoneFlagBadge,
    TimezoneRemoveButton,
    TimezonePickerWrap,
    TimezonePickerButton,
    TimezonePickerPanel,
    TimezonePickerResults,
    TimezonePickerGroupHeader,
    TimezoneOptionButton,
    SuggestionsFeedSection,
    SuggestionsFeedHeader,
    SuggestionsFeedCards,
    SuggestionFeedCard,
    SuggestionFeedCardHeader,
    SuggestionIndicator,
    SuggestionFeedBody,
    SuggestionFeedTitle,
    SuggestionFeedMeta,
    SuggestionDismissButton,
    SuggestionFeedActions,
    SuggestionActionButton,
    SuggestionsRefreshButton,
    RightTabs,
    RightTab,
    RightPanelInner,
    TodoHeading,
    ProgressWrap,
    ProgressBar,
    ProgressFill,
    AnalyticsButton,
    WeatherStrip,
    WeatherCard,
    WeatherUnavailableCard,
    WeatherHeadline,
    TaskList,
    TaskCard,
    TaskCardIndicator,
    TaskCheck,
    TaskCheckIcon,
    TaskTitle,
    TaskMeta,
    TaskTimeText,
    FeatureCard,
    FeatureIcon,
    TodayItem,
    StatBox,
    TaskFieldInput,
    ReminderPopupStack,
    ReminderPopupCard,
    ReminderPopupTop,
    ReminderPopupTitle,
    ReminderPopupCaption,
    ReminderPopupDismiss,
} from "./components";
import {
    createCalendarItem,
    createCalendarItemType,
    deleteCalendarItem,
    deleteCalendarItemType,
    fetchCalendarItemTypes,
    getCalendarApiErrorMessage,
    getCalendarCompanyUsers,
    getCalendarItems,
    getCalendarPublicHolidayMap,
    getCalendarPublicHolidayVisibility,
    getCalendarSettings,
    getCalendarTimezone,
    getCalendarWeatherForecast,
    getCalendarWeatherLocationForTimezone,
    getDetectedCalendarLocation,
    getFallbackCalendarLocation,
    getLocationSubdivisionCode,
    hasStoredCalendarTimezone,
    saveCalendarPublicHolidayVisibility,
    saveCalendarTimezone,
    updateCalendarItem,
    updateCalendarItemType,
    updateCalendarSettings,
} from "./api";
import {
    AUDIENCE_COLORS,
    AUTOMATION_ITEM_TYPE_COLORS,
    AUTOMATION_ITEM_TYPE_LABEL,
    AUTOMATION_ITEM_TYPES,
    EVENT_SOURCE,
    EVENT_SOURCE_LABEL,
    formatTimezoneLabel,
    getCalendarItemSourceKey,
    getCalendarItemSourceLabel,
    getCalendarItemSourceReferenceId,
    getTimezonePlaceName,
    getTimezoneSearchText,
    majorTimezoneOptions,
    SHARED_AUDIENCE,
    SOURCE_COLORS,
    TASK_PRIORITIES,
    TASK_REPEAT_OPTIONS,
    TASK_TYPE_COLORS,
    TASK_TYPE_LABEL,
    TASK_TYPES,
    timezoneOptions,
} from "./calendarData";
import { getItemTypeLabel } from "./itemTypes";
import type {
    AnalyticsPeriod,
    AnalyticsVisibility,
    CalendarConnection,
    CalendarItem,
    CalendarUser,
    CompanyUser,
    CompanyUsersStatus,
    CustomItemType,
    EventSource,
    LocationInfo,
    ManagedItemType,
    ReminderPopup,
    RightTabOption,
    SharedAudience,
    SuggestionFeedItem,
    TaskPrefill,
    ThemeMode,
    ViewOption,
    WeatherForecastDay,
    WeatherStatus,
    WeekSpanLane,
} from "./types";
import {
    priorityTone,
    priorityLabel,
    smartFeatures,
    VIEW_OPTIONS,
    VISIBLE_RIGHT_TABS,
    ANALYTICS_PERIOD_OPTIONS,
    ANALYTICS_VISIBILITY_OPTIONS,
    getCustomGroupNameLower,
    getAudienceOptionsForUser,
    isManualActionCalendarItem,
    SuggestionCandidateDraft,
    getUserString,
    hasScheduledCalendarFields,
    hasActionCalendarFields,
    getCalendarItemMode,
    canManageSharedCalendarItems,
    canManageCalendarAudience,
    getAutomationFeature,
    getAutomationTaskTypeFilterKey,
    getScheduledItemTaskTypeFilterKey,
    isScheduledItemVisible,
    isTaskVisible,
    getItemPermissions,
    scheduledItemOverlapsDate,
    getTaskStartDay,
    getTaskEndDay,
    taskOverlapsDate,
    expandRepeatingCalendarItems,
    isMultiDayTask,
    getTaskDateTimeRange,
    getReminderOffsetMinutes,
    getReminderAnchorDateTime,
    getReminderPopupKey,
    formatReminderItemTime,
    buildBusyWindows,
    buildWeekSpanLanes,
    getMonthWeekItemsOffset,
    getMonthWeekRowHeight,
    getTimezoneDisplayName,
    getTimezoneFlag,
    getTimezoneOffsetInfo,
    getCurrentDateInTimezone,
    getDateInTimezone,
    getDateTimeInTimezone,
    getHolidayFooterLabel,
    formatScheduledItemTime,
    formatTaskDueTime,
    buildPrefillFromSlot,
    getDateSeries,
    findFirstSuggestedSlot,
    findPreferredSlot,
    getItemStartValue,
    monthMatrix,
    weekMatrix,
    yearMatrix,
    HOUR_LABELS,
    getViewStep,
    getViewHeading,
    getAnalyticsVisibilityLabel,
    withAlpha,
    getCalendarPillColors,
    getCalendarItemDisplayColor,
    getAnalyticsColorGradient,
    getAnalyticsSourceGradient,
    getAnalyticsBarHeight,
    getAnalyticsPeriodRange,
    getAnalyticsTrendBuckets,
    getAnalyticsHeading,
    getCalendarItemsDateRange,
    scheduledItemTouchesHour,
    isSurveyOrPollAutomationItem,
    automationEndpointOverlapsDate,
    automationEndpointTouchesHour,
    getAutomationEndpointTitle,
} from "./utils/calendarHelpers";
import { DetailModal, CalendarItemModal, ItemTypeManagerModal } from "./components/Modals";
import { IsFeatureReady } from "src/constants/features";

type CalendarProps = {
    user?: CalendarUser | null;
};

type LeftPanelHintKey = "visibility" | "itemTypes" | "automations" | "timezoneCalculator" | "connectedCalendars";

type LeftPanelInfoHint = {
    title: string;
    body: string;
};

const ANALYTICS_ALL_ITEMS = "all";

const LEFT_PANEL_INFO_HINTS: Record<LeftPanelHintKey, LeftPanelInfoHint> = {
    visibility: {
        title: "Visibility",
        body: "Filter the calendar by who an item is visible to. Turning a group off hides matching items from the current view.",
    },
    itemTypes: {
        title: "Item Types",
        body: "Show, hide, or manage manual item types after they have loaded from the Calendar API.",
    },
    automations: {
        title: "Automations",
        body: "Your calendar can display automation items created through other Me Business features, so important activity appears here without needing to be added manually. Use these filters to show or hide available automation sources.",
    },
    timezoneCalculator: {
        title: "Timezone Calculator",
        body: "Add attendee timezones here, then use the New Item form to find meeting times with the best working-hours overlap.",
    },
    connectedCalendars: {
        title: "Connected Calendars",
        body: "Control which linked calendar sources appear in this view. Toggle a source off to temporarily hide its items.",
    },
};

const AUTOMATION_FILTER_OPTIONS: string[] = [
    AUTOMATION_ITEM_TYPES.NOTICEBOARD,
    AUTOMATION_ITEM_TYPES.GOAL_TRACKING,
    AUTOMATION_ITEM_TYPES.HABIT,
    AUTOMATION_ITEM_TYPES.MOOD,
    AUTOMATION_ITEM_TYPES.CHECKLIST,
    AUTOMATION_ITEM_TYPES.EMAIL_CAMPAIGN,
    AUTOMATION_ITEM_TYPES.SURVEY,
    AUTOMATION_ITEM_TYPES.POLL,
];

const AUTOMATION_FEATURE_FLAGS: Record<string, string[]> = {
    [AUTOMATION_ITEM_TYPES.NOTICEBOARD]: [FEATURE_FLAGS.NOTICEBOARD],
    [AUTOMATION_ITEM_TYPES.GOAL_TRACKING]: [FEATURE_FLAGS.GOAL_TRACKER, FEATURE_FLAGS.TEAM_GOALS],
    [AUTOMATION_ITEM_TYPES.HABIT]: [FEATURE_FLAGS.HABIT_TRACKER],
    [AUTOMATION_ITEM_TYPES.MOOD]: [FEATURE_FLAGS.MOOD_TRACKER],
    [AUTOMATION_ITEM_TYPES.CHECKLIST]: [FEATURE_FLAGS.TEAM_GOALS],
    [AUTOMATION_ITEM_TYPES.EMAIL_CAMPAIGN]: [FEATURE_FLAGS.EMAIL_CAMPAIGNS],
    [AUTOMATION_ITEM_TYPES.SURVEY]: [FEATURE_FLAGS.SURVEYS],
    [AUTOMATION_ITEM_TYPES.POLL]: [FEATURE_FLAGS.POLLS],
};

export default function Calendar({ user }: CalendarProps) {
    const { themeMode } = useDashboardTheme() as { themeMode: ThemeMode };
    const [timezone, setTimezone] = useState(getCalendarTimezone);
    const [cursor, setCursor] = useState<Dayjs>(() => getCurrentDateInTimezone(getCalendarTimezone()));
    const [selectedDate, setSelectedDate] = useState<Dayjs>(() => getCurrentDateInTimezone(getCalendarTimezone()));
    const [activeView, setActiveView] = useState<ViewOption>("Month");
    const [activeRightTab, setActiveRightTab] = useState<RightTabOption>("To-Do");
    const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
    const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
    const [analyticsOpen, setAnalyticsOpen] = useState(false);
    const [analyticsPeriod, setAnalyticsPeriod] = useState<AnalyticsPeriod>("Weekly");
    const [analyticsVisibility, setAnalyticsVisibility] = useState<AnalyticsVisibility>("All");
    const [analyticsItemType, setAnalyticsItemType] = useState(ANALYTICS_ALL_ITEMS);
    const [analyticsChartsReady, setAnalyticsChartsReady] = useState(false);
    const [search, setSearch] = useState("");
    const [showCalendarItemModal, setShowCalendarItemModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [editingCalendarItem, setEditingCalendarItem] = useState<CalendarItem | null>(null);
    const [taskPrefill, setTaskPrefill] = useState<TaskPrefill | null>(null);
    const [detailItem, setDetailItem] = useState<CalendarItem | null>(null);
    const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null);
    const [weatherInfo, setWeatherInfo] = useState<WeatherForecastDay[] | null>(null);
    const [weatherStatus, setWeatherStatus] = useState<WeatherStatus>("loading");
    const [publicHolidayMap, setPublicHolidayMap] = useState<Record<string, string[]>>({});
    const [showPublicHolidays, setShowPublicHolidays] = useState(getCalendarPublicHolidayVisibility);
    const [activeReminderPopups, setActiveReminderPopups] = useState<ReminderPopup[]>([]);
    const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState<string[]>([]);
    const [suggestionRefreshTick, setSuggestionRefreshTick] = useState(0);
    const [timezoneAttendees, setTimezoneAttendees] = useState<string[]>([]);
    const [timezonePickerOpen, setTimezonePickerOpen] = useState(false);
    const [timezoneSearch, setTimezoneSearch] = useState("");
    const [leftPanelInfoHint, setLeftPanelInfoHint] = useState<{ key: LeftPanelHintKey; x: number; y: number } | null>(null);
    const [showItemTypeManager, setShowItemTypeManager] = useState(false);
    const [managedItemTypes, setManagedItemTypes] = useState<ManagedItemType[]>([]);
    const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
    const [companyUsersStatus, setCompanyUsersStatus] = useState<CompanyUsersStatus>("idle");
    const [scheduledItems, setScheduledItems] = useState<CalendarItem[]>([]);
    const [tasks, setTasks] = useState<CalendarItem[]>([]);
    const [connections, setConnections] = useState<CalendarConnection[]>([]);
    const [visibilityFilters, setVisibilityFilters] = useState<Record<SharedAudience, boolean>>({
        [SHARED_AUDIENCE.PERSONAL]: true,
        [SHARED_AUDIENCE.EMPLOYEES]: true,
        [SHARED_AUDIENCE.CLIENTS]: true,
        [SHARED_AUDIENCE.BOTH]: true,
    });
    const [taskTypeFilters, setTaskTypeFilters] = useState<Record<string, boolean>>({
        [TASK_TYPES.MEETING]: true,
        [TASK_TYPES.TASK]: true,
        [TASK_TYPES.REMINDER]: true,
        [AUTOMATION_ITEM_TYPES.NOTICEBOARD]: true,
        [AUTOMATION_ITEM_TYPES.GOAL_TRACKING]: true,
        [AUTOMATION_ITEM_TYPES.HABIT]: true,
        [AUTOMATION_ITEM_TYPES.MOOD]: true,
        [AUTOMATION_ITEM_TYPES.CHECKLIST]: true,
        [AUTOMATION_ITEM_TYPES.EMAIL_CAMPAIGN]: true,
        [AUTOMATION_ITEM_TYPES.SURVEY]: true,
        [AUTOMATION_ITEM_TYPES.POLL]: true,
    });
    const dismissedReminderKeysRef = useRef<Set<string>>(new Set());
    const companyUsersRequestRef = useRef<AbortController | null>(null);
    const calendarSettingsLoadedRef = useRef(false);
    const timezonePersistenceReadyRef = useRef(false);
    const timezonePickerRef = useRef<HTMLDivElement | null>(null);
    const calendarItemsDateRange = useMemo(() => getCalendarItemsDateRange({
        activeView,
        analyticsOpen,
        analyticsPeriod,
        cursor,
        selectedDate,
    }), [activeView, analyticsOpen, analyticsPeriod, cursor, selectedDate]);
    const calendarItemsRangeParams = useMemo(() => ({
        from: calendarItemsDateRange.start.toISOString(),
        to: calendarItemsDateRange.end.toISOString(),
    }), [calendarItemsDateRange.end, calendarItemsDateRange.start]);

    const showCalendarApiError = useCallback((error: unknown, fallback: string) => {
        console.error(error);
        Toast.error(getCalendarApiErrorMessage(error, fallback));
    }, []);

    const loadCalendarItems = useCallback(async () => {
        try {
            const items = await getCalendarItems(calendarItemsRangeParams);
            const expandedItems = expandRepeatingCalendarItems(items, calendarItemsDateRange);
            setScheduledItems(expandedItems.filter(hasScheduledCalendarFields));
            setTasks(expandedItems.filter(hasActionCalendarFields));
        } catch (error) {
            showCalendarApiError(error, "Calendar items could not be loaded.");
        }
    }, [calendarItemsDateRange, calendarItemsRangeParams.from, calendarItemsRangeParams.to, showCalendarApiError]);

    const loadCompanyUsers = useCallback(() => {
        if (companyUsersStatus === "loading" || companyUsersStatus === "ready") return;

        const controller = new AbortController();
        companyUsersRequestRef.current = controller;
        setCompanyUsersStatus("loading");

        getCalendarCompanyUsers(getUserString(user, UserKeys.TOKEN), controller.signal)
            .then(({ users, status }) => {
                if (controller.signal.aborted) return;
                setCompanyUsers(users);
                setCompanyUsersStatus(status);
            })
            .catch((error) => {
                if (controller.signal.aborted || (error as Error).name === "AbortError") return;
                console.error(error);
                setCompanyUsers([]);
                setCompanyUsersStatus("error");
            });
    }, [companyUsersStatus, user]);

    useEffect(() => {
        loadCalendarItems();
    }, [loadCalendarItems]);

    useEffect(() => () => {
        companyUsersRequestRef.current?.abort();
    }, []);

    useEffect(() => {
        if ((!showCalendarItemModal && !showDetailModal) || companyUsersStatus !== "idle") return;
        loadCompanyUsers();
    }, [companyUsersStatus, loadCompanyUsers, showCalendarItemModal, showDetailModal]);

    useEffect(() => {
        let cancelled = false;

        getCalendarSettings()
            .then((settings) => {
                if (cancelled) return;
                setTimezone(settings.timezone || getCalendarTimezone());
                setTimezoneAttendees(settings.timezoneAttendees || []);
                calendarSettingsLoadedRef.current = true;
            })
            .catch((error) => {
                if (!cancelled) {
                    showCalendarApiError(error, "Calendar settings could not be loaded.");
                }
            });

        return () => {
            cancelled = true;
        };
    }, [showCalendarApiError]);

    useEffect(() => {
        let cancelled = false;

        fetchCalendarItemTypes()
            .then((nextItemTypes) => {
                if (!cancelled) setManagedItemTypes(nextItemTypes);
            })
            .catch((error) => {
                if (!cancelled) {
                    showCalendarApiError(error, "Calendar item types could not be loaded.");
                }
            });

        return () => {
            cancelled = true;
        };
    }, [showCalendarApiError]);

    useEffect(() => {
        let cancelled = false;

        getDetectedCalendarLocation()
            .then((nextLocation) => {
                if (cancelled) return;
                setLocationInfo(nextLocation);
                if (!hasStoredCalendarTimezone() && nextLocation.timezone) {
                    setTimezone(nextLocation.timezone);
                }
            })
            .catch((error) => {
                console.error(error);
                if (!cancelled) {
                    getCalendarWeatherLocationForTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
                        .then((timezoneLocation) => {
                            if (!cancelled) {
                                setLocationInfo(timezoneLocation);
                            }
                        })
                        .catch(() => {
                            if (!cancelled) {
                                setLocationInfo(getFallbackCalendarLocation());
                            }
                        });
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setWeatherStatus("loading");

        getCalendarWeatherLocationForTimezone(timezone)
            .then(weatherLocation => getCalendarWeatherForecast(weatherLocation, timezone))
            .then((forecast) => {
                if (cancelled) return;
                setWeatherInfo(forecast);
                setWeatherStatus(forecast.length > 0 ? "ready" : "unavailable");
            })
            .catch((error) => {
                console.error(error);
                if (!cancelled) {
                    setWeatherInfo(null);
                    setWeatherStatus("unavailable");
                }
            });

        return () => {
            cancelled = true;
        };
    }, [timezone]);

    useEffect(() => {
        if (!timezonePersistenceReadyRef.current) {
            timezonePersistenceReadyRef.current = true;
            return;
        }
        saveCalendarTimezone(timezone);
    }, [timezone]);
    useEffect(() => { saveCalendarPublicHolidayVisibility(showPublicHolidays); }, [showPublicHolidays]);
    useEffect(() => {
        if (!calendarSettingsLoadedRef.current) return;
        updateCalendarSettings({ attendeeTimezones: timezoneAttendees }).catch((error) => {
            showCalendarApiError(error, "Calendar timezone settings could not be saved.");
        });
    }, [showCalendarApiError, timezoneAttendees]);
    useEffect(() => {
        setTaskTypeFilters(prev => {
            const next = { ...prev };
            managedItemTypes.forEach((itemType) => {
                if (next[itemType.id] === undefined) {
                    next[itemType.id] = true;
                }
                if (next[itemType.itemTypeUuid] === undefined) {
                    next[itemType.itemTypeUuid] = true;
                }
            });
            return next;
        });
    }, [managedItemTypes]);
    useEffect(() => {
        const selectedItemTypeExists = managedItemTypes.some(itemType => itemType.itemTypeUuid === analyticsItemType || itemType.id === analyticsItemType);
        if (analyticsItemType !== ANALYTICS_ALL_ITEMS && !selectedItemTypeExists) {
            setAnalyticsItemType(ANALYTICS_ALL_ITEMS);
        }
    }, [analyticsItemType, managedItemTypes]);
    useEffect(() => {
        const customTypeById = new Map(managedItemTypes.map(itemType => [itemType.itemTypeUuid, itemType]));
        let changed = false;
        const nextTasks = tasks.map((task) => {
            const itemType = customTypeById.get(task.itemTypeUuid || task.taskType);
            if (!itemType || task.color === itemType.color) return task;
            changed = true;
            return { ...task, color: itemType.color };
        });
        if (changed) {
            setTasks(nextTasks);
        }
    }, [managedItemTypes, scheduledItems, tasks]);
    useEffect(() => {
        if (!analyticsOpen) {
            setAnalyticsChartsReady(false);
            return undefined;
        }

        setAnalyticsChartsReady(false);
        const frameId = window.requestAnimationFrame(() => setAnalyticsChartsReady(true));
        return () => window.cancelAnimationFrame(frameId);
    }, [analyticsItemType, analyticsOpen, analyticsPeriod, analyticsVisibility, selectedDate]);

    useEffect(() => {
        setDismissedSuggestionIds([]);
    }, [selectedDate]);

    useEffect(() => {
        if (!timezonePickerOpen) return undefined;

        const handleOutsideClick = (event) => {
            if (timezonePickerRef.current && !timezonePickerRef.current.contains(event.target)) {
                setTimezonePickerOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [timezonePickerOpen]);

    useEffect(() => {
        if (!leftPanelInfoHint) return undefined;

        const closeHint = () => setLeftPanelInfoHint(null);
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeHint();
            }
        };

        document.addEventListener("mousedown", closeHint);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("scroll", closeHint, true);
        window.addEventListener("resize", closeHint);
        return () => {
            document.removeEventListener("mousedown", closeHint);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("scroll", closeHint, true);
            window.removeEventListener("resize", closeHint);
        };
    }, [leftPanelInfoHint]);

    const toggleLeftPanelInfoHint = (key: LeftPanelHintKey, event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setLeftPanelInfoHint(prev => prev?.key === key
            ? null
            : {
                key,
                x: Math.max(16, Math.min(rect.left, window.innerWidth - 276)),
                y: Math.max(16, rect.top - 8),
            });
    };

    const renderLeftPanelSectionHeading = (label: string, hintKey: LeftPanelHintKey, onAction?: () => void, actionLabel = "Edit") => (
        <SectionHeadingRow>
            <SectionLabel>{label}</SectionLabel>
            <SectionHeadingActions>
                {onAction && (
                    <SectionEditButton type="button" onClick={onAction}>
                        {actionLabel}
                    </SectionEditButton>
                )}
                <SectionInfoButton
                    type="button"
                    active={leftPanelInfoHint?.key === hintKey}
                    aria-label={`About ${label}`}
                    aria-expanded={leftPanelInfoHint?.key === hintKey}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => toggleLeftPanelInfoHint(hintKey, event)}
                >
                    i
                </SectionInfoButton>
            </SectionHeadingActions>
        </SectionHeadingRow>
    );

    const customItemTypes = useMemo<CustomItemType[]>(() => managedItemTypes.filter((itemType): itemType is CustomItemType => itemType.scope !== "builtIn"), [managedItemTypes]);
    const filterItemTypes = managedItemTypes;
    const itemTypeByValue = useMemo(() => {
        const itemTypeMap = new Map<string, ManagedItemType>();
        managedItemTypes.forEach((itemType) => {
            itemTypeMap.set(itemType.itemTypeUuid, itemType);
            itemTypeMap.set(itemType.id, itemType);
        });
        return itemTypeMap;
    }, [managedItemTypes]);
    const analyticsItemTypeOptions = useMemo(() => ([
        { value: ANALYTICS_ALL_ITEMS, label: "All Items" },
        ...managedItemTypes.map(itemType => ({
            value: itemType.itemTypeUuid,
            label: itemType.label,
        })),
    ]), [managedItemTypes]);
    const analyticsItemTypeLabel = analyticsItemTypeOptions.find(option => option.value === analyticsItemType)?.label || "All Items";
    const canManageSharedCalendar = canManageSharedCalendarItems(user);
    const audienceOptions = useMemo(() => getAudienceOptionsForUser(user), [user]);
    const availableAutomationFilterOptions = useMemo(() => {
        const featureFlags = getFeatureFlags(user);
        const companyFeatureFlags = Array.isArray(featureFlags) ? featureFlags : [];
        return AUTOMATION_FILTER_OPTIONS.filter((automationType) => {
            const requiredFeatureFlags = AUTOMATION_FEATURE_FLAGS[automationType] || [];
            return requiredFeatureFlags.some(featureFlag => companyFeatureFlags.includes(featureFlag));
        });
    }, [user]);
    const timezoneToday = useMemo(() => getCurrentDateInTimezone(timezone), [timezone]);
    const connectionMap = useMemo<Partial<Record<EventSource, boolean>>>(() => connections.reduce((acc, connection) => {
        acc[connection.source] = connection.connected;
        return acc;
    }, {} as Partial<Record<EventSource, boolean>>), [connections]);

    const reminderScheduledItems = useMemo<CalendarItem[]>(() => {
        return scheduledItems
            .filter(item => isScheduledItemVisible(item, user, connectionMap))
            .sort((a, b) => dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf());
    }, [connectionMap, scheduledItems, user]);

    const visibleScheduledItems = useMemo<CalendarItem[]>(() => {
        const nowIs = scheduledItems
            .filter(item => isScheduledItemVisible(item, user, connectionMap))
            .filter(item => visibilityFilters[item.audience])
            .filter((item) => {
                const filterKey = getScheduledItemTaskTypeFilterKey(item);
                return !filterKey || taskTypeFilters[filterKey] !== false;
            })
            .sort((a, b) => dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf());
        return nowIs;
    }, [connectionMap, scheduledItems, taskTypeFilters, user, visibilityFilters]);

    const visibleTasks = useMemo<CalendarItem[]>(() => {
        return tasks
            .filter(task => isTaskVisible(task, user))
            .filter(task => visibilityFilters[task.audience])
            .filter((task) => {
                const itemType = managedItemTypes.find(type => type.itemTypeUuid === (task.itemTypeUuid || task.taskType) || type.id === task.taskType);
                const automationFilterKey = getAutomationTaskTypeFilterKey(task);
                const filterKeys = [automationFilterKey, task.itemTypeUuid, task.taskType, itemType?.id, itemType?.itemTypeUuid].filter(Boolean);
                return filterKeys.length === 0 || filterKeys.every(key => taskTypeFilters[key] !== false);
            });
    }, [managedItemTypes, taskTypeFilters, tasks, user, visibilityFilters]);

    const reminderTasks = useMemo<CalendarItem[]>(() => {
        return tasks
            .filter(task => isTaskVisible(task, user))
            .filter(task => !task.completed);
    }, [tasks, user]);

    useEffect(() => {
        const updateReminderPopups = () => {
            const now = dayjs();
            const nextPopups = [...reminderScheduledItems, ...reminderTasks]
                .map((item) => {
                    const reminderMinutes = getReminderOffsetMinutes(item.reminderOffset);
                    const anchor = getReminderAnchorDateTime(item);
                    const popupKey = getReminderPopupKey(item);

                    if (reminderMinutes === null || !anchor?.isValid() || !popupKey || dismissedReminderKeysRef.current.has(popupKey)) {
                        return null;
                    }

                    const reminderAt = anchor.subtract(reminderMinutes, "minute");
                    if (now.isBefore(reminderAt) || now.isAfter(anchor.add(15, "minute"))) {
                        return null;
                    }

                    return {
                        key: popupKey,
                        itemTitle: item.title,
                        timeLabel: formatReminderItemTime(item, timezone),
                        sortValue: reminderAt.valueOf(),
                    };
                })
                .filter((popup): popup is ReminderPopup => Boolean(popup))
                .sort((left, right) => left.sortValue - right.sortValue)
                .slice(0, 3);

            setActiveReminderPopups((prev) => (
                prev.length === nextPopups.length
                && prev.every((popup, index) => popup.key === nextPopups[index]?.key)
                    ? prev
                    : nextPopups
            ));
        };

        updateReminderPopups();
        const intervalId = window.setInterval(updateReminderPopups, 30000);
        return () => window.clearInterval(intervalId);
    }, [reminderScheduledItems, reminderTasks, timezone]);
    const analyticsVisibilityMap = useMemo<Record<AnalyticsVisibility, SharedAudience[]>>(() => ({
        All: [
            SHARED_AUDIENCE.PERSONAL,
            SHARED_AUDIENCE.EMPLOYEES,
            SHARED_AUDIENCE.CLIENTS,
            SHARED_AUDIENCE.BOTH,
        ],
        Personal: [SHARED_AUDIENCE.PERSONAL],
        Employees: [SHARED_AUDIENCE.EMPLOYEES],
        Clients: [SHARED_AUDIENCE.CLIENTS],
        Both: [SHARED_AUDIENCE.BOTH, SHARED_AUDIENCE.EMPLOYEES, SHARED_AUDIENCE.CLIENTS],
    }), []);

    const searchResults = useMemo<CalendarItem[]>(() => {
        const term = search.trim().toLowerCase();
        if (!term) return [];
        const combined = [...visibleScheduledItems, ...visibleTasks];
        return combined.filter(item => `${item.title} ${item.description || ""}`.toLowerCase().includes(term)).slice(0, 8);
    }, [search, visibleScheduledItems, visibleTasks]);

    const timezonePickerOptions = useMemo<string[]>(() => {
        const term = timezoneSearch.trim().toLowerCase();
        return majorTimezoneOptions
            .filter(option => !timezoneAttendees.includes(option))
            .filter(option => !term || getTimezoneSearchText(option).toLowerCase().includes(term));
    }, [timezoneAttendees, timezoneSearch]);

    const timezonePickerGroups = useMemo(() => {
        const groups = new Map<string, { offsetLabel: string; offsetMinutes: number; options: string[] }>();
        timezonePickerOptions.forEach((option) => {
            const offset = getTimezoneOffsetInfo(option);
            const group = groups.get(offset.label) || {
                offsetLabel: offset.label,
                offsetMinutes: offset.minutes,
                options: [],
            };
            group.options.push(option);
            groups.set(offset.label, group);
        });
        return Array.from(groups.values())
            .map(group => ({
                ...group,
                options: group.options.sort((left, right) => getTimezoneDisplayName(left).localeCompare(getTimezoneDisplayName(right))),
            }))
            .sort((left, right) => left.offsetMinutes - right.offsetMinutes || left.offsetLabel.localeCompare(right.offsetLabel));
    }, [timezonePickerOptions]);

    const addTimezoneAttendee = (nextTimezone: string) => {
        setTimezoneAttendees(prev => Array.from(new Set([...prev, nextTimezone])));
        setTimezoneSearch("");
        setTimezonePickerOpen(false);
    };

    const removeTimezoneAttendee = (targetTimezone: string) => {
        setTimezoneAttendees(prev => prev.filter(item => item !== targetTimezone));
    };

    const monthDays = useMemo(() => monthMatrix(cursor), [cursor]);
    const monthWeeks = useMemo(() => Array.from({ length: 6 }, (_, index) => monthDays.slice(index * 7, index * 7 + 7)), [monthDays]);
    const weekDays = useMemo(() => weekMatrix(cursor), [cursor]);
    const yearMonths = useMemo(() => yearMatrix(cursor), [cursor]);
    const locationSubdivisionCode = useMemo(() => getLocationSubdivisionCode(locationInfo), [locationInfo]);
    const visibleHolidayYears = useMemo(() => {
        const days = activeView === "Year"
            ? yearMonths.flatMap((month) => monthMatrix(month).slice(0, 42))
            : activeView === "Week"
                ? weekDays
                : activeView === "Day"
                    ? [selectedDate]
                    : monthDays;
        return Array.from(new Set(days.map((day) => day.year()))).sort((left, right) => left - right);
    }, [activeView, monthDays, selectedDate, weekDays, yearMonths]);

    useEffect(() => {
        if (!locationInfo?.countryCode || visibleHolidayYears.length === 0) {
            setPublicHolidayMap({});
            return undefined;
        }

        let cancelled = false;
        getCalendarPublicHolidayMap({
            countryCode: locationInfo.countryCode,
            subdivisionCode: locationSubdivisionCode,
            years: visibleHolidayYears,
        })
            .then((holidayMap) => {
                if (!cancelled) {
                    setPublicHolidayMap(holidayMap);
                }
            })
            .catch((error) => {
                console.error(error);
                if (!cancelled) {
                    setPublicHolidayMap({});
                }
            });

        return () => {
            cancelled = true;
        };
    }, [locationInfo?.countryCode, locationSubdivisionCode, visibleHolidayYears]);

    const tasksForSelectedDate = useMemo<CalendarItem[]>(() => {
        return visibleTasks.filter(task => task.dueDate === selectedDate.format("YYYY-MM-DD"));
    }, [selectedDate, visibleTasks]);

    const itemTypeTasksForSelectedDate = useMemo<CalendarItem[]>(() => {
        return tasksForSelectedDate.filter(isManualActionCalendarItem);
    }, [tasksForSelectedDate]);

    const isSingleDayEvent = useCallback((item: CalendarItem) => (
        getDateInTimezone(item.startAt, timezone).isSame(getDateInTimezone(item.endAt, timezone), "day")
    ), [timezone]);

    const monthSpanItems = useMemo<CalendarItem[]>(() => {
        const multiDayScheduledItems = visibleScheduledItems.filter((item) => !isSingleDayEvent(item) && !isSurveyOrPollAutomationItem(item));
        const multiDayTasks = visibleTasks.filter((task) => isMultiDayTask(task) && !isSurveyOrPollAutomationItem(task));
        return [...multiDayScheduledItems, ...multiDayTasks];
    }, [isSingleDayEvent, visibleScheduledItems, visibleTasks]);

    const weekSpanItems = useMemo<CalendarItem[]>(() => {
        const allDayOrMultiDayScheduledItems = visibleScheduledItems.filter((item) => (
            (item.allDay || !isSingleDayEvent(item))
            && !isSurveyOrPollAutomationItem(item)
        ));
        const allDayOrMultiDayTasks = visibleTasks.filter((task) => (task.allDay || isMultiDayTask(task)) && !isSurveyOrPollAutomationItem(task));
        return [...allDayOrMultiDayScheduledItems, ...allDayOrMultiDayTasks];
    }, [isSingleDayEvent, visibleScheduledItems, visibleTasks]);

    const weekSpanLanes = useMemo<WeekSpanLane[]>(() => buildWeekSpanLanes(weekDays, weekSpanItems, timezone), [timezone, weekDays, weekSpanItems]);
    const weekAllDayRowHeight = useMemo(() => Math.max(44, (weekSpanLanes.length || 1) * 28 + 10), [weekSpanLanes]);

    const todaysTasksProgress = useMemo(() => {
        const total = itemTypeTasksForSelectedDate.length;
        const done = itemTypeTasksForSelectedDate.filter(task => task.completed).length;
        const percent = total ? Math.round((done / total) * 100) : 0;
        return { total, done, percent };
    }, [itemTypeTasksForSelectedDate]);

    const selectedDateScheduledItems = useMemo<CalendarItem[]>(() => visibleScheduledItems.filter(item => isSurveyOrPollAutomationItem(item)
        ? automationEndpointOverlapsDate(item, selectedDate, timezone)
        : scheduledItemOverlapsDate(item, selectedDate, timezone)), [selectedDate, timezone, visibleScheduledItems]);
    const selectedDateTimedScheduledItems = useMemo<CalendarItem[]>(() => selectedDateScheduledItems.filter(item => !item.allDay), [selectedDateScheduledItems]);
    const selectedDateAllDayScheduledItems = useMemo<CalendarItem[]>(() => selectedDateScheduledItems.filter(item => item.allDay), [selectedDateScheduledItems]);
    const selectedDatePanelItems = useMemo<CalendarItem[]>(() => {
        const panelScheduledItems = visibleScheduledItems.filter(item => isSurveyOrPollAutomationItem(item)
            ? automationEndpointOverlapsDate(item, selectedDate, timezone)
            : scheduledItemOverlapsDate(item, selectedDate, timezone));
        const panelTasks = visibleTasks.filter(task => isSurveyOrPollAutomationItem(task)
            ? automationEndpointOverlapsDate(task, selectedDate, timezone)
            : taskOverlapsDate(task, selectedDate));
        return [...panelScheduledItems, ...panelTasks]
            .sort((left, right) => getItemStartValue(left, timezone) - getItemStartValue(right, timezone));
    }, [selectedDate, timezone, visibleScheduledItems, visibleTasks]);

    const upcomingItems = useMemo<CalendarItem[]>(() => {
        return [...selectedDateScheduledItems, ...tasksForSelectedDate].slice(0, 4);
    }, [selectedDateScheduledItems, tasksForSelectedDate]);

    const weekStats = useMemo(() => {
        const start = selectedDate.startOf("week");
        const end = selectedDate.endOf("week");
        const weeklyScheduledItems = visibleScheduledItems.filter(item => !getDateTimeInTimezone(item.endAt, timezone).isBefore(start) && !getDateTimeInTimezone(item.startAt, timezone).isAfter(end));
        const meetingHours = weeklyScheduledItems.reduce((sum, item) => sum + dayjs(item.endAt).diff(dayjs(item.startAt), "minute") / 60, 0);
        const weeklyTasks = visibleTasks.filter(task => {
            const due = dayjs(task.dueDate);
            return !due.isBefore(start, "day") && !due.isAfter(end, "day");
        });
        return {
            meetingHours: meetingHours.toFixed(1),
            focusHours: Math.max(0, Math.round(weeklyTasks.filter(task => task.taskType === AUTOMATION_ITEM_TYPES.GOAL_TRACKING).length * 1.5 * 10) / 10),
            completed: weeklyTasks.filter(task => task.completed).length,
            total: weeklyTasks.length,
        };
    }, [selectedDate, timezone, visibleScheduledItems, visibleTasks]);

    const suggestionCandidates = useMemo<SuggestionFeedItem[]>(() => {
        const userEmail = getUserString(user, UserKeys.EMAIL);
        const currentWeekDays = weekMatrix(selectedDate);
        const nextWeekDays = weekMatrix(selectedDate.add(1, "week"));
        const upcomingDays = getDateSeries(selectedDate, 10);
        const weekStart = currentWeekDays[0].startOf("day");
        const weekEnd = currentWeekDays[6].endOf("day");
        const nextWeekStart = nextWeekDays[0].startOf("day");
        const nextWeekEnd = nextWeekDays[6].endOf("day");
        const currentWeekTasks = visibleTasks.filter((task) => {
            const start = getTaskStartDay(task);
            const end = getTaskEndDay(task);
            return !end.isBefore(weekStart, "day") && !start.isAfter(weekEnd, "day");
        });
        const currentWeekScheduledItems = visibleScheduledItems.filter((item) => {
            const start = dayjs(item.startAt);
            const end = dayjs(item.endAt);
            return !end.isBefore(weekStart) && !start.isAfter(weekEnd);
        });
        const nextWeekItems = [
            ...visibleTasks.filter((task) => {
                const start = getTaskStartDay(task);
                const end = getTaskEndDay(task);
                return !end.isBefore(nextWeekStart, "day") && !start.isAfter(nextWeekEnd, "day");
            }),
            ...visibleScheduledItems.filter((item) => {
                const start = dayjs(item.startAt);
                const end = dayjs(item.endAt);
                return !end.isBefore(nextWeekStart) && !start.isAfter(nextWeekEnd);
            }),
        ];
        const getItemsForDay = (day: Dayjs): CalendarItem[] => {
            const scheduledItemsForDay = visibleScheduledItems.filter((item) => scheduledItemOverlapsDate(item, day, timezone));
            const tasksForDay = visibleTasks.filter((task) => taskOverlapsDate(task, day));
            return [...scheduledItemsForDay, ...tasksForDay].sort((left, right) => getItemStartValue(left, timezone) - getItemStartValue(right, timezone));
        };
        const currentWeekItemsByDay = currentWeekDays.map((day) => ({ day, items: getItemsForDay(day) }));
        const personalItemsThisWeek = currentWeekItemsByDay.flatMap(({ items }) => items).filter((item) => item.audience === SHARED_AUDIENCE.PERSONAL || item.ownerEmail === userEmail);
        const projectItemsThisWeek = currentWeekTasks.filter((task) => task.title.toLowerCase().includes("project"));
        const meetingTasksThisWeek = currentWeekTasks.filter((task) => task.taskType === TASK_TYPES.MEETING);
        const meetingItemsThisWeek = [
            ...currentWeekScheduledItems.map((item) => ({ start: dayjs(item.startAt), end: dayjs(item.endAt), day: dayjs(item.startAt).startOf("day"), item })),
            ...meetingTasksThisWeek.map((task) => {
                const range = getTaskDateTimeRange(task);
                return { start: range.start, end: range.end, day: range.start.startOf("day"), item: task };
            }),
        ];
        const completedMorning = visibleTasks.filter((task) => task.completed && getTaskDateTimeRange(task).start.hour() < 12).length;
        const completedAfternoon = visibleTasks.filter((task) => task.completed && getTaskDateTimeRange(task).start.hour() >= 12).length;
        const existingTitles = visibleTasks.map((task) => task.title.toLowerCase());
        const suggestions: SuggestionFeedItem[] = [];
        const pushSuggestion = (suggestion: SuggestionCandidateDraft) => {
            if (!suggestion?.prefill) return;
            if (suggestions.some((entry) => entry.id === suggestion.id)) return;
            suggestions.push({ ...suggestion, prefill: suggestion.prefill });
        };

        if (visibleTasks.length === 0 && visibleScheduledItems.length === 0) {
            return [];
        }

        if (personalItemsThisWeek.length === 0) {
            const slot = findFirstSuggestedSlot({ days: upcomingDays, taskType: TASK_TYPES.TASK, visibleScheduledItems, visibleTasks });
            pushSuggestion({
                id: "personal-time",
                icon: "🌿",
                indicatorColor: "#22c55e",
                indicatorBackground: "rgba(34,197,94,0.14)",
                text: "You haven’t scheduled personal time this week",
                actionLabel: "Schedule Personal Time",
                meta: "Use an open gap to protect a short personal block.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Personal Time",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.MEDIUM,
                }),
            });
        }

        let consecutiveLateNights = null;
        let currentStreak = [];
        currentWeekItemsByDay.forEach(({ day, items }) => {
            const hasLateNight = items.some((item) => {
                const endValue = hasScheduledCalendarFields(item) ? dayjs(item.endAt) : getTaskDateTimeRange(item).end;
                return endValue.hour() >= 19;
            });
            if (hasLateNight) {
                currentStreak.push(day);
                if (currentStreak.length >= 3) consecutiveLateNights = currentStreak.slice(-3);
            } else {
                currentStreak = [];
            }
        });
        if (consecutiveLateNights?.length >= 3) {
            const recoveryDay = consecutiveLateNights[consecutiveLateNights.length - 1].add(1, "day");
            const slot = findPreferredSlot({
                day: recoveryDay,
                durationMinutes: 30,
                preferredHour: 9,
                taskType: TASK_TYPES.REMINDER,
                visibleScheduledItems,
                visibleTasks,
            });
            pushSuggestion({
                id: "recovery-time",
                icon: "🌙",
                indicatorColor: "#8b5cf6",
                indicatorBackground: "rgba(139,92,246,0.16)",
                text: "You have 3 late nights in a row — adjust?",
                actionLabel: "Add Recovery Time",
                meta: "A lighter personal block can help reset the pace.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Recovery Time",
                    taskType: TASK_TYPES.REMINDER,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.MEDIUM,
                }),
            });
        }

        const hasFocusBlockThisWeek = currentWeekTasks.some((task) => task.title.toLowerCase().includes("focus"));
        if (!hasFocusBlockThisWeek) {
            const slot = findFirstSuggestedSlot({ days: upcomingDays, taskType: TASK_TYPES.TASK, visibleScheduledItems, visibleTasks });
            pushSuggestion({
                id: "focus-time",
                icon: "🎯",
                indicatorColor: "#5b6ef5",
                indicatorBackground: "rgba(91,110,245,0.14)",
                text: "Your week has no dedicated focus block yet",
                actionLabel: "Add Focus Time",
                meta: "Protect one deeper work block before the week fills up.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Focus Time",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: currentWeekTasks.length >= 8 ? TASK_PRIORITIES.HIGH : TASK_PRIORITIES.MEDIUM,
                }),
            });
        }

        const backToBackPattern = (() => {
            for (const day of currentWeekDays) {
                const meetings = meetingItemsThisWeek
                    .filter((entry) => entry.day.isSame(day, "day"))
                    .sort((left, right) => left.start.valueOf() - right.start.valueOf());
                for (let index = 0; index < meetings.length - 1; index += 1) {
                    const gap = meetings[index + 1].start.diff(meetings[index].end, "minute");
                    if (gap >= 0 && gap <= 15) {
                        return { day, gap, end: meetings[index].end };
                    }
                }
            }
            return null;
        })();
        if (backToBackPattern) {
            const slot = backToBackPattern.gap > 0
                ? {
                    startDate: backToBackPattern.end.format("YYYY-MM-DD"),
                    startTime: backToBackPattern.end.format("HH:mm"),
                    endDate: backToBackPattern.end.add(Math.min(30, Math.max(backToBackPattern.gap, 15)), "minute").format("YYYY-MM-DD"),
                    endTime: backToBackPattern.end.add(Math.min(30, Math.max(backToBackPattern.gap, 15)), "minute").format("HH:mm"),
                }
                : findPreferredSlot({
                    day: backToBackPattern.day,
                    durationMinutes: 15,
                    preferredHour: Math.min(backToBackPattern.end.hour() + 1, 17),
                    taskType: TASK_TYPES.REMINDER,
                    visibleScheduledItems,
                    visibleTasks,
                });
            pushSuggestion({
                id: "buffer-time",
                icon: "⏳",
                indicatorColor: "#f59e0b",
                indicatorBackground: "rgba(245,158,11,0.16)",
                text: "You have back-to-back meetings on this day",
                actionLabel: "Add Buffer Time",
                meta: `A short buffer can break up the load on ${backToBackPattern.day.format("ddd")}.`,
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Buffer Time",
                    taskType: TASK_TYPES.REMINDER,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.MEDIUM,
                }),
            });
        }

        if (projectItemsThisWeek.length === 0) {
            const slot = findFirstSuggestedSlot({ days: upcomingDays, taskType: TASK_TYPES.TASK, visibleScheduledItems, visibleTasks });
            pushSuggestion({
                id: "project-work",
                icon: "🛠",
                indicatorColor: "#10b981",
                indicatorBackground: "rgba(16,185,129,0.16)",
                text: "You haven’t scheduled any project time for this week",
                actionLabel: "Schedule Project Work",
                meta: "Add a block for longer-range work before meetings take over.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Project Work",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: currentWeekTasks.length >= 8 ? TASK_PRIORITIES.HIGH : TASK_PRIORITIES.MEDIUM,
                }),
            });
        }

        const overloadedDay = currentWeekItemsByDay.find(({ day, items }) => {
            const busyMinutes = buildBusyWindows({ selectedDay: day, visibleScheduledItems, visibleTasks })
                .reduce((sum, window) => sum + window.end.diff(window.start, "minute"), 0);
            return items.length >= 6 || busyMinutes >= 480;
        });
        if (overloadedDay) {
            const slot = findFirstSuggestedSlot({
                days: getDateSeries(overloadedDay.day.add(1, "day"), 4),
                taskType: TASK_TYPES.TASK,
                visibleScheduledItems,
                visibleTasks,
            });
            pushSuggestion({
                id: "review-day-plan",
                icon: "🗓",
                indicatorColor: "#ef4444",
                indicatorBackground: "rgba(239,68,68,0.14)",
                text: "This day looks overloaded — spread items out?",
                actionLabel: "Review Day Plan",
                meta: `${overloadedDay.day.format("dddd")} is carrying a heavy schedule load.`,
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Reset / Admin Time",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.MEDIUM,
                }),
            });
        }

        const noBreakPattern = currentWeekItemsByDay.find(({ day }) => {
            const busyWindows = buildBusyWindows({ selectedDay: day, visibleScheduledItems, visibleTasks });
            return busyWindows.some((window, index) => {
                const nextWindow = busyWindows[index + 1];
                if (!nextWindow) return false;
                const gap = nextWindow.start.diff(window.end, "minute");
                const combined = nextWindow.end.diff(window.start, "minute");
                return gap <= 20 && combined >= 180;
            });
        });
        if (noBreakPattern) {
            const slot = findPreferredSlot({
                day: noBreakPattern.day,
                durationMinutes: 30,
                preferredHour: 12,
                preferredMinute: 30,
                taskType: TASK_TYPES.REMINDER,
                visibleScheduledItems,
                visibleTasks,
            });
            pushSuggestion({
                id: "break-time",
                icon: "☕",
                indicatorColor: "#fb7185",
                indicatorBackground: "rgba(251,113,133,0.16)",
                text: "You haven’t added a break between long work blocks",
                actionLabel: "Add Break",
                meta: "A short break can soften a run of long scheduled work.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Break",
                    taskType: TASK_TYPES.REMINDER,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.LOW,
                }),
            });
        }

        const friday = currentWeekDays.find((day) => day.day() === 5);
        const hasWeeklyReview = existingTitles.some((title) => title.includes("weekly review"));
        if (friday && getItemsForDay(friday).length <= 2 && !hasWeeklyReview) {
            const slot = findPreferredSlot({
                day: friday,
                durationMinutes: 45,
                preferredHour: 16,
                taskType: TASK_TYPES.TASK,
                visibleScheduledItems,
                visibleTasks,
            });
            pushSuggestion({
                id: "weekly-review",
                icon: "📝",
                indicatorColor: "#06b6d4",
                indicatorBackground: "rgba(6,182,212,0.16)",
                text: "You have free space on Friday — use it for planning?",
                actionLabel: "Add Weekly Review",
                meta: "End the week with a short planning and reflection block.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Weekly Review",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.MEDIUM,
                    repeat: TASK_REPEAT_OPTIONS.EVERY_WEEK,
                }),
            });
        }

        const hasAdminTime = existingTitles.some((title) => title.includes("admin time"));
        if (!hasAdminTime) {
            const slot = findFirstSuggestedSlot({ days: upcomingDays, taskType: TASK_TYPES.TASK, visibleScheduledItems, visibleTasks });
            pushSuggestion({
                id: "admin-time",
                icon: "📁",
                indicatorColor: "#64748b",
                indicatorBackground: "rgba(100,116,139,0.16)",
                text: "Your calendar has no personal admin time scheduled",
                actionLabel: "Add Admin Time",
                meta: "Reserve a short slot for the small tasks that tend to pile up.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Admin Time",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.MEDIUM,
                }),
            });
        }

        const hasFollowUp = existingTitles.some((title) => title.includes("follow-up"));
        if (meetingItemsThisWeek.length >= 3 && !hasFollowUp) {
            const lastMeeting = meetingItemsThisWeek.sort((left, right) => right.end.valueOf() - left.end.valueOf())[0];
            const slot = findPreferredSlot({
                day: lastMeeting.day,
                durationMinutes: 45,
                preferredHour: Math.min(lastMeeting.end.hour() + 1, 17),
                taskType: TASK_TYPES.TASK,
                visibleScheduledItems,
                visibleTasks,
            });
            pushSuggestion({
                id: "follow-up-time",
                icon: "✉️",
                indicatorColor: "#7c3aed",
                indicatorBackground: "rgba(124,58,237,0.16)",
                text: "You scheduled several meetings but no follow-up time",
                actionLabel: "Add Follow-Up Block",
                meta: "Capture actions while the meetings are still fresh.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Follow-Up Time",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.MEDIUM,
                }),
            });
        }

        if (completedMorning >= completedAfternoon + 2) {
            const tomorrowMorning = getDateSeries(selectedDate.add(1, "day"), 5);
            const slot = findFirstSuggestedSlot({ days: tomorrowMorning, taskType: TASK_TYPES.TASK, visibleScheduledItems, visibleTasks });
            pushSuggestion({
                id: "best-time",
                icon: "🌅",
                indicatorColor: "#f59e0b",
                indicatorBackground: "rgba(245,158,11,0.16)",
                text: "You usually complete more items in the morning",
                actionLabel: "Use Best Time",
                meta: "Lean into your stronger completion window with a morning block.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: hasFocusBlockThisWeek ? "Priority Task" : "Focus Time",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.HIGH,
                }),
            });
        }

        if (nextWeekItems.length < 2) {
            const slot = friday
                ? findPreferredSlot({
                    day: friday,
                    durationMinutes: 45,
                    preferredHour: 15,
                    taskType: TASK_TYPES.TASK,
                    visibleScheduledItems,
                    visibleTasks,
                })
                : findPreferredSlot({
                    day: nextWeekStart,
                    durationMinutes: 45,
                    preferredHour: 9,
                    taskType: TASK_TYPES.TASK,
                    visibleScheduledItems,
                    visibleTasks,
                });
            pushSuggestion({
                id: "plan-next-week",
                icon: "📆",
                indicatorColor: "#5b6ef5",
                indicatorBackground: "rgba(91,110,245,0.16)",
                text: "You haven’t planned next week yet",
                actionLabel: "Plan Next Week",
                meta: "A short planning session can reduce next-week decision drag.",
                prefill: buildPrefillFromSlot({
                    slot,
                    title: "Weekly Planning",
                    taskType: TASK_TYPES.TASK,
                    audience: SHARED_AUDIENCE.PERSONAL,
                    priority: TASK_PRIORITIES.MEDIUM,
                    repeat: TASK_REPEAT_OPTIONS.EVERY_WEEK,
                }),
            });
        }

        return suggestions;
    }, [selectedDate, timezone, user, visibleScheduledItems, visibleTasks]);

    const visibleSuggestions = useMemo(() => {
        if (suggestionCandidates.length === 0) return [];
        const rotation = suggestionCandidates.length > 0 ? suggestionRefreshTick % suggestionCandidates.length : 0;
        const ordered = [
            ...suggestionCandidates.slice(rotation),
            ...suggestionCandidates.slice(0, rotation),
        ];
        return ordered
            .filter((suggestion) => !dismissedSuggestionIds.includes(suggestion.id))
            .slice(0, 4);
    }, [dismissedSuggestionIds, suggestionCandidates, suggestionRefreshTick]);

    const analyticsRange = useMemo(() => getAnalyticsPeriodRange(analyticsPeriod, selectedDate), [analyticsPeriod, selectedDate]);
    const analyticsIncludesToday = useMemo(() => (
        !timezoneToday.startOf("day").isBefore(analyticsRange.start, "day")
        && !timezoneToday.startOf("day").isAfter(analyticsRange.end, "day")
    ), [analyticsRange.end, analyticsRange.start, timezoneToday]);
    const analyticsDataRange = useMemo(() => (
        analyticsIncludesToday
            ? { start: analyticsRange.start, end: timezoneToday.endOf("day") }
            : analyticsRange
    ), [analyticsIncludesToday, analyticsRange, timezoneToday]);
    const getTaskAnalyticsItemTypeValue = useCallback((task: CalendarItem): string => task.itemTypeUuid || task.taskType || "", []);
    const getTaskAnalyticsItemTypeMeta = useCallback((task: CalendarItem) => {
        const value = getTaskAnalyticsItemTypeValue(task);
        const itemType = value ? itemTypeByValue.get(value) : undefined;
        const fallbackKey = value || `item-${task.id}`;
        const fallbackColor = task.color || (task.taskType ? TASK_TYPE_COLORS[task.taskType] || AUTOMATION_ITEM_TYPE_COLORS[task.taskType] : undefined) || "var(--calendar-accent)";
        const fallbackLabel = task.taskType ? TASK_TYPE_LABEL[task.taskType] || AUTOMATION_ITEM_TYPE_LABEL[task.taskType] : "Uncategorised";

        return {
            key: itemType?.itemTypeUuid || fallbackKey,
            label: itemType?.label || fallbackLabel,
            color: itemType?.color || fallbackColor,
            gradient: getAnalyticsColorGradient(itemType?.color || fallbackColor),
        };
    }, [getTaskAnalyticsItemTypeValue, itemTypeByValue]);
    const analyticsItemTypeBreakdown = useMemo(() => {
        const itemTypeCounts = new Map<string, {
            key: string;
            label: string;
            color: string;
            gradient: string;
            count: number;
        }>();

        managedItemTypes.forEach((itemType) => {
            itemTypeCounts.set(itemType.itemTypeUuid, {
                key: itemType.itemTypeUuid,
                label: itemType.label,
                color: itemType.color,
                gradient: getAnalyticsColorGradient(itemType.color),
                count: 0,
            });
        });

        return itemTypeCounts;
    }, [managedItemTypes]);

    const analyticsTasks = useMemo(() => {
        const allowedAudiences = analyticsVisibilityMap[analyticsVisibility] || [];
        const tasksInRange = visibleTasks.filter((task) => {
            if (allowedAudiences.length === 0) return false;
            if (!allowedAudiences.includes(task.audience)) return false;
            const start = getTaskStartDay(task);
            const end = getTaskEndDay(task);
            return !end.isBefore(analyticsDataRange.start, "day") && !start.isAfter(analyticsDataRange.end, "day");
        });

        if (analyticsItemType === ANALYTICS_ALL_ITEMS) return tasksInRange;
        return tasksInRange.filter(task => getTaskAnalyticsItemTypeValue(task) === analyticsItemType);
    }, [analyticsDataRange.end, analyticsDataRange.start, analyticsVisibility, analyticsVisibilityMap, analyticsItemType, getTaskAnalyticsItemTypeValue, visibleTasks]);

    const analyticsScheduledItems = useMemo(() => {
        if (analyticsItemType !== ANALYTICS_ALL_ITEMS) return [];
        const allowedAudiences = analyticsVisibilityMap[analyticsVisibility] || [];
        return visibleScheduledItems.filter((item) => {
            if (allowedAudiences.length === 0) return false;
            if (!allowedAudiences.includes(item.audience)) return false;
            const start = dayjs(item.startAt);
            const end = dayjs(item.endAt);
            return !end.isBefore(analyticsDataRange.start) && !start.isAfter(analyticsDataRange.end);
        });
    }, [analyticsDataRange.end, analyticsDataRange.start, analyticsVisibility, analyticsVisibilityMap, analyticsItemType, visibleScheduledItems]);

    const analyticsSummary = useMemo(() => {
        const totalItems = analyticsTasks.length + analyticsScheduledItems.length;
        const completedTasks = analyticsTasks.filter(task => task.completed).length;
        const completionRate = totalItems ? Math.round((completedTasks / Math.max(analyticsTasks.length, 1)) * 100) : 0;
        const taskHours = analyticsTasks
            .reduce((sum, task) => {
                const range = getTaskDateTimeRange(task);
                return sum + (range.end.diff(range.start, "minute") / 60);
            }, 0);
        const scheduledHours = analyticsScheduledItems.reduce((sum, item) => sum + dayjs(item.endAt).diff(dayjs(item.startAt), "minute") / 60, 0);
        const overdueItems = analyticsTasks.filter(task => !task.completed && getTaskEndDay(task).isBefore(selectedDate, "day")).length;
        const plannedTaskHours = analyticsTasks.length * 1.25;
        const achievedTaskPercent = plannedTaskHours ? Math.round((taskHours / plannedTaskHours) * 100) : 0;
        const overdueRatio = totalItems ? overdueItems / totalItems : 0;
        const completionComponent = analyticsTasks.length ? completionRate * 0.78 : (totalItems ? 55 : 0);
        const taskTimeComponent = Math.min(achievedTaskPercent, 100) * 0.22;
        const outcomeScore = Math.max(0, Math.min(100, Math.round(completionComponent + taskTimeComponent - (overdueRatio * 35))));
        const visibilitySplit = {
            personal: analyticsTasks.filter(task => task.audience === SHARED_AUDIENCE.PERSONAL).length + analyticsScheduledItems.filter(item => item.audience === SHARED_AUDIENCE.PERSONAL).length,
            shared: analyticsTasks.filter(task => task.audience !== SHARED_AUDIENCE.PERSONAL).length + analyticsScheduledItems.filter(item => item.audience !== SHARED_AUDIENCE.PERSONAL).length,
        };
        const typeCounts = new Map(Array.from(analyticsItemTypeBreakdown.entries()).map(([key, item]) => [key, { ...item }]));
        analyticsTasks.forEach((task) => {
            const meta = getTaskAnalyticsItemTypeMeta(task);
            const existing = typeCounts.get(meta.key);
            typeCounts.set(meta.key, existing ? {
                ...existing,
                count: existing.count + 1,
            } : {
                ...meta,
                count: 1,
            });
        });
        if (analyticsScheduledItems.length > 0) {
            typeCounts.set("scheduled-items", {
                key: "scheduled-items",
                label: "Scheduled Items",
                color: SOURCE_COLORS[EVENT_SOURCE.MANUAL],
                gradient: getAnalyticsSourceGradient(EVENT_SOURCE.MANUAL),
                count: analyticsScheduledItems.length,
            });
        }
        const byType = Array.from(typeCounts.values())
            .filter(item => item.count > 0)
            .sort((left, right) => (right.count - left.count) || left.label.localeCompare(right.label));
        const getAnalyticsSourceMeta = (item) => {
            const sourceKey = getCalendarItemSourceKey(item.source);
            if (sourceKey) {
                return {
                    key: sourceKey,
                    label: getCalendarItemSourceLabel(item.source, EVENT_SOURCE_LABEL[sourceKey] || sourceKey),
                    color: SOURCE_COLORS[sourceKey] || item.color || "var(--calendar-accent)",
                    gradient: getAnalyticsSourceGradient(sourceKey, item.color),
                };
            }

            return {
                key: EVENT_SOURCE.MANUAL,
                label: EVENT_SOURCE_LABEL[EVENT_SOURCE.MANUAL],
                color: SOURCE_COLORS[EVENT_SOURCE.MANUAL],
                gradient: getAnalyticsSourceGradient(EVENT_SOURCE.MANUAL),
            };
        };
        const sourceCounts = new Map();
        [...analyticsTasks, ...analyticsScheduledItems].forEach((item) => {
            const meta = getAnalyticsSourceMeta(item);
            const existing = sourceCounts.get(meta.key);
            sourceCounts.set(meta.key, existing ? {
                ...existing,
                count: existing.count + 1,
            } : {
                ...meta,
                count: 1,
            });
        });
        const bySource = Array.from(sourceCounts.values())
            .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
        const trendBuckets = getAnalyticsTrendBuckets(analyticsPeriod, selectedDate);
        const trend = trendBuckets.map((bucket) => {
            const breakdown = byType.map((typeMeta) => {
                const taskCount = analyticsTasks.filter((task) => (
                    getTaskAnalyticsItemTypeMeta(task).key === typeMeta.key
                    && !getTaskStartDay(task).isBefore(bucket.start)
                    && !getTaskStartDay(task).isAfter(bucket.end)
                )).length;
                const scheduledCount = typeMeta.key === "scheduled-items"
                    ? analyticsScheduledItems.filter((item) => {
                        const day = dayjs(item.startAt);
                        return !day.isBefore(bucket.start) && !day.isAfter(bucket.end);
                    }).length
                    : 0;
                const count = taskCount + scheduledCount;
                return {
                    ...typeMeta,
                    count,
                };
            });
            const total = breakdown.reduce((sum, item) => sum + item.count, 0);
            return {
                label: bucket.label,
                title: bucket.title,
                breakdown,
                total,
            };
        });
        const trendMaxTotal = Math.max(1, ...trend.map((point) => point.total));
        const scheduledVsCompleted = [
            {
                key: "scheduled-items",
                label: "Scheduled Items",
                color: SOURCE_COLORS[EVENT_SOURCE.MANUAL],
                gradient: getAnalyticsSourceGradient(EVENT_SOURCE.MANUAL),
                count: analyticsScheduledItems.length,
                note: `${Number(scheduledHours.toFixed(1))}h booked`,
            },
            {
                key: "completed-work",
                label: "Completed Work",
                color: "#10b981",
                gradient: getAnalyticsColorGradient("#10b981"),
                count: completedTasks,
                note: `${Number(taskHours.toFixed(1))}h scheduled`,
            },
        ];
        const scheduledVsCompletedMax = Math.max(1, ...scheduledVsCompleted.map((item) => item.count));
        const typeBreakdownMax = Math.max(1, ...byType.map((item) => item.count));
        const productivityStreak = Math.max(1, Math.min(9, Math.round(completedTasks / 2) || 1));
        const burnoutRisk = scheduledHours > (taskHours * 1.4) ? "High" : scheduledHours > taskHours ? "Moderate" : "Low";
        const recommendation = scheduledHours > taskHours
            ? "Scheduled time is outpacing task item time. Protect a focused work block."
            : taskHours < 2
                ? "Task time is trending low. Add one longer work block this period."
                : "Planning balance is healthy. Keep repeating the current calendar item rhythm.";

        return {
            totalItems,
            completedTasks,
            completionRate,
            outcomeScore,
            focusHours: Number(taskHours.toFixed(1)),
            plannedFocusHours: Number(plannedTaskHours.toFixed(1)),
            achievedFocusPercent: achievedTaskPercent,
            meetingLoad: analyticsScheduledItems.length,
            meetingHours: Number(scheduledHours.toFixed(1)),
            taskHours: Number(taskHours.toFixed(1)),
            productiveTasks: completedTasks,
            overdueItems,
            visibilitySplit,
            byType,
            bySource,
            trend,
            trendMaxTotal,
            meetingVsCompleted: scheduledVsCompleted,
            meetingVsCompletedMax: scheduledVsCompletedMax,
            typeBreakdownMax,
            productivityStreak,
            burnoutRisk,
            recommendation,
        };
    }, [analyticsScheduledItems, analyticsItemTypeBreakdown, analyticsPeriod, analyticsTasks, getTaskAnalyticsItemTypeMeta, selectedDate]);

    const analyticsRecommendations = useMemo(() => {
        const recommendations = [];
        const pushRecommendation = (recommendation) => {
            if (!recommendation || recommendations.some(item => item.id === recommendation.id)) return;
            recommendations.push(recommendation);
        };

        if (analyticsSummary.overdueItems > 0) {
            pushRecommendation({
                id: "analytics-catch-up",
                icon: "🧹",
                indicatorColor: "#fb7185",
                indicatorBackground: "rgba(251,113,133,0.16)",
                text: analyticsPeriod === "Weekly"
                    ? "A few things from this week still need a proper finish."
                    : analyticsPeriod === "Monthly"
                        ? "This month has a small backlog that is worth clearing before it lingers."
                        : "There are some unfinished items in this yearly view that deserve a clean catch-up block.",
                meta: analyticsSummary.overdueItems === 1
                    ? "One overdue item is enough to keep quietly draining attention. A short catch-up block would tidy it up."
                    : `${analyticsSummary.overdueItems} overdue items are still hanging around here. Give yourself one focused block to clear the oldest ones first.`,
            });
        }

        if (analyticsSummary.meetingHours > Math.max(analyticsSummary.taskHours, 1.5) || analyticsSummary.burnoutRisk === "High") {
            pushRecommendation({
                id: "analytics-focus-block",
                icon: "🛡",
                indicatorColor: "#10b981",
                indicatorBackground: "rgba(16,185,129,0.16)",
                text: analyticsPeriod === "Weekly"
                    ? "This week is leaning heavily toward scheduled items."
                    : analyticsPeriod === "Monthly"
                        ? "This month has more scheduled time than task time."
                        : "This year-to-date view has more scheduled time than task time.",
                meta: "Protecting one real task block would give the important work some breathing room again.",
            });
        }

        if (analyticsSummary.focusHours < Math.max(analyticsSummary.plannedFocusHours * 0.65, analyticsPeriod === "Weekly" ? 1.5 : analyticsPeriod === "Monthly" ? 3 : 8)) {
            pushRecommendation({
                id: "analytics-project-time",
                icon: "🌿",
                indicatorColor: "#06b6d4",
                indicatorBackground: "rgba(6,182,212,0.16)",
                text: analyticsPeriod === "Weekly"
                    ? "There is not much task time in this week yet."
                    : analyticsPeriod === "Monthly"
                        ? "This month would benefit from one deeper task block."
                        : "This yearly view could use more intentional task time to balance the pace.",
                meta: `You have logged ${analyticsSummary.focusHours}h of task time so far. A longer block would help move the meaningful work, not just the urgent work.`,
            });
        }

        if (analyticsSummary.completionRate < 55 && analyticsTasks.length >= 4) {
            pushRecommendation({
                id: "analytics-review-block",
                icon: "📝",
                indicatorColor: "#8b5cf6",
                indicatorBackground: "rgba(139,92,246,0.18)",
                text: analyticsPeriod === "Weekly"
                    ? "This week would feel lighter with a quick review and reset."
                    : analyticsPeriod === "Monthly"
                        ? "This month is asking for a short mid-course review."
                        : "This yearly view would benefit from a more deliberate review point.",
                meta: `The completion rate is sitting at ${analyticsSummary.completionRate}%. A short review block would help you decide what to finish, move, or let go of.`,
            });
        }

        if (recommendations.length === 0) {
            pushRecommendation({
                id: "analytics-keep-rhythm",
                icon: "✨",
                indicatorColor: "#f59e0b",
                indicatorBackground: "rgba(245,158,11,0.18)",
                text: analyticsPeriod === "Weekly"
                    ? "This week is in decent shape. A light planning block would help you keep that rhythm."
                    : analyticsPeriod === "Monthly"
                        ? "This month looks fairly balanced. A small planning checkpoint would keep it that way."
                        : "This yearly view feels steady enough to build on. A planning block would help you stay intentional.",
                meta: "Nothing here is flashing red. This is a good moment to plan the next stretch before the calendar gets noisy again.",
            });
        }

        return recommendations.slice(0, analyticsPeriod === "Yearly" ? 3 : 2);
    }, [
        analyticsPeriod,
        analyticsRange.end,
        analyticsSummary.burnoutRisk,
        analyticsSummary.completionRate,
        analyticsSummary.focusHours,
        analyticsSummary.meetingHours,
        analyticsSummary.overdueItems,
        analyticsSummary.plannedFocusHours,
        analyticsSummary.taskHours,
        analyticsTasks.length,
        selectedDate,
    ]);

    const openNewCalendarItemModal = (prefill: TaskPrefill | null = null) => {
        setEditingCalendarItem(null);
        setTaskPrefill(prefill);
        if (prefill?.startDate) {
            const prefillDate = dayjs(prefill.startDate);
            setSelectedDate(prefillDate);
            setCursor(prefillDate);
        }
        setShowCalendarItemModal(true);
    };

    const closeCalendarItemModal = () => {
        setShowCalendarItemModal(false);
        setEditingCalendarItem(null);
        setTaskPrefill(null);
    };

    const openDetail = (item: CalendarItem) => {
        setDetailItem(item);
        setShowDetailModal(true);
    };

    const navigateInApp = (path: string) => {
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
    };

    const getAutomationReferenceId = (item: CalendarItem): string => (
        getCalendarItemSourceReferenceId(item.source)
        || ("sourceReferenceId" in item ? item.sourceReferenceId || "" : "")
        || item.automationDetail?.sourceReferenceId
        || ""
    );

    const getNoticeboardAudienceFilter = (item: CalendarItem): "employee" | "client" => {
        const detail = item.automationDetail || {};
        const recipients = String(detail.recipients || detail.userGroup || "").toLowerCase();
        if (recipients.includes("client")) return "client";
        if (recipients.includes("employee")) return "employee";
        if (item.audience === SHARED_AUDIENCE.CLIENTS) return "client";
        return "employee";
    };

    const openAutomationFeature = (item: CalendarItem) => {
        const automationFeature = getAutomationFeature(item);
        const sourceReferenceId = getAutomationReferenceId(item);

        if ((automationFeature === EVENT_SOURCE.SURVEY || automationFeature === EVENT_SOURCE.POLL) && !sourceReferenceId) {
            Toast.error("This calendar item is missing its source reference.");
            return;
        }

        if (automationFeature === EVENT_SOURCE.NOTICEBOARD) {
            setShowDetailModal(false);
            navigateInApp(`/noticeboard?noticeboard=${getNoticeboardAudienceFilter(item)}`);
            return;
        }

        if (automationFeature === EVENT_SOURCE.EMAIL_CAMPAIGN) {
            setShowDetailModal(false);
            navigateInApp("/email-campaigns?emailCampaigns=scheduled");
            return;
        }

        if (automationFeature === EVENT_SOURCE.SURVEY) {
            setShowDetailModal(false);
            navigateInApp(`/survey?viewSurveyUuid=${encodeURIComponent(sourceReferenceId)}`);
            return;
        }

        if (automationFeature === EVENT_SOURCE.POLL) {
            setShowDetailModal(false);
            navigateInApp(`/survey?viewPollUuid=${encodeURIComponent(sourceReferenceId)}`);
            return;
        }

        Toast.error("Opening this source feature from Calendar is not available yet.");
    };

    const handleSaveCalendarItem = async (item: CalendarItem) => {
        const wasEditing = Boolean(editingCalendarItem);
        const editingSharedItem = Boolean(editingCalendarItem && editingCalendarItem.audience !== SHARED_AUDIENCE.PERSONAL);
        if (!canManageCalendarAudience(user, item.audience)
            || (editingSharedItem && !canManageCalendarAudience(user, editingCalendarItem?.audience || SHARED_AUDIENCE.PERSONAL))) {
            Toast.error("Only users with Calendar permission can save shared calendar items.");
            return;
        }
        try {
            if (wasEditing) {
                await updateCalendarItem(item.id, item);
            } else {
                await createCalendarItem(item);
            }
            await loadCalendarItems();
            closeCalendarItemModal();
            Toast.success(wasEditing ? "Calendar item updated." : "Calendar item created.");
        } catch (error) {
            showCalendarApiError(error, wasEditing ? "Calendar item could not be updated." : "Calendar item could not be created.");
        }
    };

    const handleDeleteCalendarItem = async (item: CalendarItem) => {
        const deletedItemUuid = item.itemUuid || item.id;
        try {
            await deleteCalendarItem(deletedItemUuid);
            setScheduledItems(previous => previous.filter(calendarItem => (
                (calendarItem.itemUuid || calendarItem.id) !== deletedItemUuid
            )));
            setTasks(previous => previous.filter(calendarItem => (
                (calendarItem.itemUuid || calendarItem.id) !== deletedItemUuid
            )));
            closeCalendarItemModal();
            Toast.success("Calendar item deleted.");
            await loadCalendarItems();
        } catch (error) {
            showCalendarApiError(error, "Calendar item could not be deleted.");
        }
    };

    const handleCreateItemType = async (itemType: CustomItemType) => {
        if (itemType.scope === "company" && !canManageSharedCalendar) {
            Toast.error("Only users with Calendar permission can create company-wide item types.");
            return false;
        }
        try {
            const createdItemType = await createCalendarItemType(itemType);
            setManagedItemTypes(prev => [...prev, createdItemType]);
            Toast.success("Item type added.");
            return true;
        } catch (error) {
            showCalendarApiError(error, "Item type could not be added.");
            return false;
        }
    };

    const handleUpdateItemType = async (itemType: CustomItemType) => {
        const previousItemType = customItemTypes.find(currentItemType => currentItemType.itemTypeUuid === itemType.itemTypeUuid);
        if (!canManageSharedCalendar && (itemType.scope === "company" || previousItemType?.scope === "company")) {
            Toast.error("Only users with Calendar permission can manage company-wide item types.");
            return false;
        }
        try {
            const updatedItemType = await updateCalendarItemType(itemType);
            setManagedItemTypes(prev => prev.map(currentItemType => currentItemType.itemTypeUuid === itemType.itemTypeUuid ? updatedItemType : currentItemType));
            Toast.success("Item type updated.");
            return true;
        } catch (error) {
            showCalendarApiError(error, "Item type could not be updated.");
            return false;
        }
    };

    const handleDeleteItemType = async (itemType: CustomItemType) => {
        if (itemType.scope === "company" && !canManageSharedCalendar) {
            Toast.error("Only users with Calendar permission can manage company-wide item types.");
            return false;
        }
        try {
            await deleteCalendarItemType(itemType.itemTypeUuid);
            setManagedItemTypes(prev => prev.filter(currentItemType => currentItemType.itemTypeUuid !== itemType.itemTypeUuid));
            Toast.success("Item type deleted.");
            return true;
        } catch (error) {
            showCalendarApiError(error, "Item type could not be deleted.");
            return false;
        }
    };

    const openItemEdit = (item: CalendarItem) => {
        setShowDetailModal(false);
        const permissions = getItemPermissions(item, user);
        if (!permissions.canEdit) {
            Toast.error("This item is read-only.");
            return;
        }
        setTaskPrefill(null);
        setEditingCalendarItem(item);
        setShowCalendarItemModal(true);
    };

    const handleSuggestionDismiss = (suggestionId: string) => {
        setDismissedSuggestionIds((prev) => Array.from(new Set([...prev, suggestionId])));
    };

    const handleSuggestionAction = (suggestion: SuggestionFeedItem) => {
        handleSuggestionDismiss(suggestion.id);
        openNewCalendarItemModal(suggestion.prefill);
    };

    const dismissReminderPopup = (popupKey: string) => {
        dismissedReminderKeysRef.current.add(popupKey);
        setActiveReminderPopups((prev) => prev.filter((popup) => popup.key !== popupKey));
    };

    const toggleTaskDone = async (task: CalendarItem) => {
        const updatedTask: CalendarItem = {
            ...task,
            completed: !task.completed,
            status: !task.completed ? "done" as const : "active" as const,
            progress: !task.completed ? 100 : 0,
            updatedAt: dayjs().toISOString(),
        };

        try {
            await updateCalendarItem(task.id, updatedTask);
            setTasks(tasks.map(item => item.id === task.id ? updatedTask : item));
        } catch (error) {
            showCalendarApiError(error, "Task status could not be updated.");
        }
    };

    const locationLabel = useMemo<string>(() => {
        if (!locationInfo) return "Locating...";
        if (locationInfo.city === "Unknown location") return "Location unavailable";
        const place = [locationInfo.city, locationInfo.region || locationInfo.countryCode].filter(Boolean).join(", ");
        return place || "Location unavailable";
    }, [locationInfo]);
    const weatherLocationLabel = useMemo<string>(() => getTimezonePlaceName(timezone), [timezone]);

    const resetTimezoneToLocation = () => {
        if (!locationInfo?.timezone) {
            Toast.error("Location timezone is unavailable.");
            return;
        }
        setTimezone(locationInfo.timezone);
        Toast.success(`Timezone reset to ${formatTimezoneLabel(locationInfo.timezone)}.`);
    };

    const selectedDateLabel = selectedDate.format("ddd D MMMM");
    const calendarHeading = getViewHeading(activeView, cursor);
    const analyticsHeading = getAnalyticsHeading(analyticsPeriod, selectedDate);
    const viewStep = getViewStep(activeView);
    const analyticsFocusProgress = Math.max(0, Math.min(100, analyticsSummary.plannedFocusHours > 0 ? analyticsSummary.achievedFocusPercent : (analyticsSummary.focusHours > 0 ? 100 : 0)));
    const analyticsFocusCircumference = 2 * Math.PI * 58;
    const analyticsFocusOffset = analyticsFocusCircumference * (1 - (analyticsFocusProgress / 100));
    const navigateCalendar = (direction: 1 | -1) => {
        const delta = direction * viewStep.amount;
        const nextCursor = cursor.add(delta, viewStep.unit);
        setCursor(nextCursor);

        if (activeView === "Day") {
            setSelectedDate(selectedDate.add(delta, viewStep.unit));
            return;
        }

        if (activeView === "Week") {
            setSelectedDate(selectedDate.add(direction, "week"));
        }
    };
    const navigateAnalytics = (direction: 1 | -1) => {
        const nextDate = analyticsPeriod === "Yearly"
            ? selectedDate.add(direction, "year")
            : analyticsPeriod === "Monthly"
                ? selectedDate.add(direction, "month")
                : selectedDate.add(direction, "week");
        setSelectedDate(nextDate);
        setCursor(nextDate);
    };

    return (
        <Surface themeMode={themeMode}>
            {leftPanelInfoHint && (
                <SectionInfoPopover
                    role="dialog"
                    aria-label={LEFT_PANEL_INFO_HINTS[leftPanelInfoHint.key].title}
                    onMouseDown={(event) => event.stopPropagation()}
                    style={{
                        left: leftPanelInfoHint.x,
                        top: leftPanelInfoHint.y,
                        transform: "translateY(-100%)",
                    }}
                >
                    <div style={{ fontWeight: 900, marginBottom: 4 }}>{LEFT_PANEL_INFO_HINTS[leftPanelInfoHint.key].title}</div>
                    <div>{LEFT_PANEL_INFO_HINTS[leftPanelInfoHint.key].body}</div>
                </SectionInfoPopover>
            )}
            {activeReminderPopups.length > 0 && (
                <ReminderPopupStack>
                    {activeReminderPopups.map((popup) => (
                        <ReminderPopupCard key={popup.key}>
                            <ReminderPopupTop>
                                <div>
                                    <ReminderPopupTitle>Reminder</ReminderPopupTitle>
                                    <ReminderPopupCaption>{popup.itemTitle} · {popup.timeLabel}</ReminderPopupCaption>
                                </div>
                                <ReminderPopupDismiss type="button" aria-label="Dismiss reminder" onClick={() => dismissReminderPopup(popup.key)}>×</ReminderPopupDismiss>
                            </ReminderPopupTop>
                        </ReminderPopupCard>
                    ))}
                </ReminderPopupStack>
            )}
            <StickyHeader>
                <HeaderBar>
                    <PageName>Calendar</PageName>
                    <HeaderTools>
                        <SearchWrap>
                            <SearchInput
                                placeholder="Search calendar items"
                                value={search}
                                onChange={({ target: { value } }) => setSearch(value)}
                            />
                            {searchResults.length > 0 && (
                                <SearchResults>
                                    {searchResults.map(item => (
                                        <SearchResultButton key={item.id} onClick={() => { openDetail(item); setSearch(""); }}>
                                            <div style={{ fontWeight: 700 }}>{item.title}</div>
                                            <SmallText>{hasActionCalendarFields(item) ? "Calendar Item" : getCalendarItemSourceLabel(item.source, "Calendar Item")}</SmallText>
                                        </SearchResultButton>
                                    ))}
                                </SearchResults>
                            )}
                        </SearchWrap>
                        <AnalyticsButton type="button" active={analyticsOpen} onClick={() => setAnalyticsOpen(prev => !prev)}>
                            {analyticsOpen ? "Back to Calendar" : "Analytics"}
                        </AnalyticsButton>
                        <LocationPill
                            type="button"
                            title={`${locationLabel} · Click to reset timezone to your location`}
                            onClick={resetTimezoneToLocation}
                        >
                            {locationLabel}
                        </LocationPill>
                        <CalendarSelectWrap>
                            <GhostSelect value={timezone} onChange={({ target: { value } }) => setTimezone(value)}>
                                {timezoneOptions.map(option => <option key={option} value={option}>{formatTimezoneLabel(option)}</option>)}
                            </GhostSelect>
                            <CalendarSelectChevron aria-hidden="true">›</CalendarSelectChevron>
                        </CalendarSelectWrap>
                    </HeaderTools>
                </HeaderBar>
            </StickyHeader>

            <Layout $leftCollapsed={leftPanelCollapsed} $rightCollapsed={rightPanelCollapsed}>
                <LeftPanel $collapsed={leftPanelCollapsed}>
                    {leftPanelCollapsed ? (
                        <CollapsedPanelButton
                            $side="left"
                            type="button"
                            aria-label="Expand filters panel"
                            aria-expanded={!leftPanelCollapsed}
                            title="Expand filters panel"
                            onClick={() => setLeftPanelCollapsed(false)}
                        >
                            <PanelCollapseGlyph aria-hidden="true">›</PanelCollapseGlyph>
                            <CollapsedPanelLabel $side="left">Filters</CollapsedPanelLabel>
                        </CollapsedPanelButton>
                    ) : (
                        <>
                            <PanelHeaderRow>
                                <PanelHeaderTitle>Filters</PanelHeaderTitle>
                                <PanelCollapseButton
                                    type="button"
                                    aria-label="Collapse filters panel"
                                    aria-expanded={!leftPanelCollapsed}
                                    title="Collapse filters panel"
                                    onClick={() => {
                                        setLeftPanelInfoHint(null);
                                        setLeftPanelCollapsed(true);
                                    }}
                                >
                                    <PanelCollapseGlyph aria-hidden="true">‹</PanelCollapseGlyph>
                                </PanelCollapseButton>
                            </PanelHeaderRow>
                            {analyticsOpen ? (
                                <>
                                    <div>
                                        <SectionLabel>Analytics Window</SectionLabel>
                                        <ChipRow style={{ marginTop: 12 }}>
                                            {ANALYTICS_PERIOD_OPTIONS.map(option => (
                                                <FilterChip key={option} type="button" active={analyticsPeriod === option} onClick={() => setAnalyticsPeriod(option)}>
                                                    {option}
                                                </FilterChip>
                                            ))}
                                        </ChipRow>
                                    </div>
                                    <div>
                                        <SectionLabel>Visibility</SectionLabel>
                                        <ChipRow style={{ marginTop: 12 }}>
                                            {ANALYTICS_VISIBILITY_OPTIONS.map(option => (
                                                <FilterChip key={option} type="button" active={analyticsVisibility === option} onClick={() => setAnalyticsVisibility(option)}>
                                                    {getAnalyticsVisibilityLabel(option)}
                                                </FilterChip>
                                            ))}
                                        </ChipRow>
                                    </div>
                                    <div>
                                        <SectionLabel>Item Type</SectionLabel>
                                        <ChipRow style={{ marginTop: 12 }}>
                                            {analyticsItemTypeOptions.map(option => (
                                                <FilterChip key={option.value} type="button" active={analyticsItemType === option.value} onClick={() => setAnalyticsItemType(option.value)}>
                                                    {option.label}
                                                </FilterChip>
                                            ))}
                                        </ChipRow>
                                    </div>
                                    <div>
                                        <SectionLabel>Connected Sources</SectionLabel>
                                        <FilterRow style={{ marginTop: 12 }}>
                                            {connections.map(connection => (
                                                <ConnectionCard key={connection.id}>
                                                    <ConnectionRow type="button" onClick={() => setConnections(prev => prev.map(item => item.id === connection.id ? { ...item, connected: !item.connected, sublabel: !item.connected ? "Linked now" : "Not connected" } : item))}>
                                                        <ConnectionLabel>
                                                            <ColorSwatch color={connection.color} />
                                                            <ConnectionText>
                                                                <ConnectionTitle>{connection.label}</ConnectionTitle>
                                                                <ConnectionMeta>{connection.connected ? "Included in analytics" : "Excluded from analytics"}</ConnectionMeta>
                                                            </ConnectionText>
                                                        </ConnectionLabel>
                                                        <ConnectionToggleWrap>
                                                            <Toggle active={connection.connected} type="button" />
                                                        </ConnectionToggleWrap>
                                                    </ConnectionRow>
                                                </ConnectionCard>
                                            ))}
                                        </FilterRow>
                                    </div>
                                </>
                            ) : (
                        <>
                            <div>
                                {renderLeftPanelSectionHeading("Visibility", "visibility")}
                                <FilterRow style={{ marginTop: 12 }}>
                                    {audienceOptions.map(({ value, label }) => (
                                        <FilterItem key={value} type="button" onClick={() => setVisibilityFilters(prev => ({ ...prev, [value]: !prev[value] }))}>
                                            <FilterLabel>
                                                <ColorSwatch color={AUDIENCE_COLORS[value]} />
                                                {label}
                                            </FilterLabel>
                                            <Toggle active={visibilityFilters[value]} type="button" />
                                        </FilterItem>
                                    ))}
                                </FilterRow>
                            </div>

                                    <div>
                                        {renderLeftPanelSectionHeading("Item Types", "itemTypes", () => setShowItemTypeManager(true))}
                                        <FilterRow style={{ marginTop: 12 }}>
                                            {filterItemTypes.map((itemType) => (
                                                <FilterItem
                                                    key={itemType.itemTypeUuid}
                                                    type="button"
                                                    onClick={() => setTaskTypeFilters((prev) => {
                                                        const nextValue = !(prev[itemType.id] !== false && prev[itemType.itemTypeUuid] !== false);
                                                        return { ...prev, [itemType.id]: nextValue, [itemType.itemTypeUuid]: nextValue };
                                                    })}
                                                >
                                                    <FilterLabel>
                                                        <ColorSwatch color={itemType.color} />
                                                        {itemType.label}
                                                    </FilterLabel>
                                                    <Toggle active={taskTypeFilters[itemType.id] !== false && taskTypeFilters[itemType.itemTypeUuid] !== false} type="button" />
                                                </FilterItem>
                                            ))}
                                        </FilterRow>
                                    </div>

                                    <div>
                                        {renderLeftPanelSectionHeading("Automations", "automations")}
                                        <FilterRow style={{ marginTop: 12 }}>
                                            {availableAutomationFilterOptions.map((key) => (
                                                <FilterItem key={key} type="button" onClick={() => setTaskTypeFilters(prev => ({ ...prev, [key]: !prev[key] }))}>
                                                    <FilterLabel>
                                                        <ColorSwatch color={AUTOMATION_ITEM_TYPE_COLORS[key]} />
                                                        {AUTOMATION_ITEM_TYPE_LABEL[key]}
                                                    </FilterLabel>
                                                    <Toggle active={taskTypeFilters[key]} type="button" />
                                                </FilterItem>
                                            ))}
                                        </FilterRow>
                                    </div>

                                    <TimezoneCalculatorCard>
                                        {renderLeftPanelSectionHeading("Timezone Calculator", "timezoneCalculator")}
                                        <SmallText style={{ display: "block" }}>Add important attendee timezones for new-item time suggestions.</SmallText>
                                        <TimezoneAttendeeList>
                                            {timezoneAttendees.map((attendeeTimezone) => (
                                                <TimezoneAttendeeRow key={attendeeTimezone}>
                                                    <TimezoneFlagBadge aria-hidden="true">{getTimezoneFlag(attendeeTimezone)}</TimezoneFlagBadge>
                                                    <div>
                                                        <div style={{ fontWeight: 800, fontSize: 13 }}>{getTimezoneDisplayName(attendeeTimezone)}</div>
                                                        <SmallText>{formatTimezoneLabel(attendeeTimezone)}</SmallText>
                                                    </div>
                                                    <TimezoneRemoveButton type="button" aria-label={`Remove ${attendeeTimezone}`} onClick={() => removeTimezoneAttendee(attendeeTimezone)}>×</TimezoneRemoveButton>
                                                </TimezoneAttendeeRow>
                                            ))}
                                            {timezoneAttendees.length === 0 && (
                                                <SmallText>No attendee timezones configured yet.</SmallText>
                                            )}
                                            <TimezonePickerWrap ref={timezonePickerRef} open={timezonePickerOpen}>
                                                <TimezonePickerButton type="button" onClick={() => setTimezonePickerOpen(prev => !prev)}>
                                                    + Add attendee timezone
                                                </TimezonePickerButton>
                                                {timezonePickerOpen && (
                                                    <TimezonePickerPanel themeMode={themeMode}>
                                                        <TaskFieldInput
                                                            themeMode={themeMode}
                                                            value={timezoneSearch}
                                                            onChange={({ target: { value } }) => setTimezoneSearch(value)}
                                                            placeholder="Search city or timezone"
                                                        />
                                                        <TimezonePickerResults>
                                                            {timezonePickerGroups.map((group) => (
                                                                <React.Fragment key={group.offsetLabel}>
                                                                    <TimezonePickerGroupHeader>
                                                                        {group.offsetLabel} · {group.options.length} {group.options.length === 1 ? "timezone" : "timezones"}
                                                                    </TimezonePickerGroupHeader>
                                                                    {group.options.map((option) => (
                                                                        <TimezoneOptionButton key={option} themeMode={themeMode} type="button" onClick={() => addTimezoneAttendee(option)}>
                                                                            <TimezoneFlagBadge aria-hidden="true">{getTimezoneFlag(option)}</TimezoneFlagBadge>
                                                                            <div>
                                                                                <div style={{ fontWeight: 800 }}>{getTimezoneDisplayName(option)}</div>
                                                                                <SmallText>{formatTimezoneLabel(option)}</SmallText>
                                                                            </div>
                                                                        </TimezoneOptionButton>
                                                                    ))}
                                                                </React.Fragment>
                                                            ))}
                                                            {timezonePickerOptions.length === 0 && (
                                                                <TimezoneOptionButton as="div" themeMode={themeMode}>
                                                                    <TimezoneFlagBadge aria-hidden="true">🌐</TimezoneFlagBadge>
                                                                    <div>
                                                                        <div style={{ fontWeight: 800 }}>No matching timezones</div>
                                                                        <SmallText>Try another city or remove an existing attendee timezone first.</SmallText>
                                                                    </div>
                                                                </TimezoneOptionButton>
                                                            )}
                                                        </TimezonePickerResults>
                                                    </TimezonePickerPanel>
                                                )}
                                            </TimezonePickerWrap>
                                        </TimezoneAttendeeList>
                                    </TimezoneCalculatorCard>

                                    <div>
                                        <SectionLabel>Public Holidays</SectionLabel>
                                        <FilterRow style={{ marginTop: 12 }}>
                                            <FilterItem type="button" onClick={() => setShowPublicHolidays((prev) => !prev)}>
                                                <FilterLabel style={{ minWidth: 0, flex: 1, textAlign: "left", marginRight: "0.9rem" }}>
                                                    <span style={{ display: "block" }}>Show public holidays for the selected location</span>
                                                </FilterLabel>
                                                <Toggle active={showPublicHolidays} type="button" />
                                            </FilterItem>
                                        </FilterRow>
                                    </div>

                                    { IsFeatureReady.calendarIntegrations() && <div>
                                        {renderLeftPanelSectionHeading("Connected Calendars", "connectedCalendars", () => Toast.success("Add connected calendar."), "Add")}
                                        <FilterRow style={{ marginTop: 12 }}>
                                            {connections.map(connection => (
                                                <ConnectionCard key={connection.id}>
                                                    <ConnectionRow type="button" onClick={() => setConnections(prev => prev.map(item => item.id === connection.id ? { ...item, connected: !item.connected, sublabel: !item.connected ? "Linked now" : "Not connected" } : item))}>
                                                        <ConnectionLabel>
                                                            <ColorSwatch color={connection.color} />
                                                            <ConnectionText>
                                                                <ConnectionTitle>{connection.label}</ConnectionTitle>
                                                                <ConnectionMeta>{connection.sublabel}</ConnectionMeta>
                                                            </ConnectionText>
                                                        </ConnectionLabel>
                                                        <ConnectionToggleWrap>
                                                            <Toggle active={connection.connected} type="button" />
                                                        </ConnectionToggleWrap>
                                                    </ConnectionRow>
                                                </ConnectionCard>
                                            ))}
                                        </FilterRow>
                                    </div> }

                                </>
                            )}
                        </>
                    )}
                </LeftPanel>

                <CenterPanel>
                    <CalendarHeader>
                        {analyticsOpen ? (
                            <>
                                <MonthHeading>
                                    <NavArrow type="button" onClick={() => navigateAnalytics(-1)}>‹</NavArrow>
                                    <div>{analyticsHeading}</div>
                                    <NavArrow type="button" onClick={() => navigateAnalytics(1)}>›</NavArrow>
                                    <TodayButton type="button" onClick={() => { setSelectedDate(timezoneToday); setCursor(timezoneToday); }}>
                                        Jump to Today
                                    </TodayButton>
                                </MonthHeading>
                                <ChipRow>
                                    <Badge background="var(--calendar-surface)" color="var(--calendar-text)">Calendar Analytics Dashboard</Badge>
                                    <Badge background="var(--calendar-accent-soft)" color="var(--calendar-accent)">{analyticsPeriod}</Badge>
                                    <Badge background="rgba(66, 231, 182, 0.16)" color="#42e7b6">{getAnalyticsVisibilityLabel(analyticsVisibility)}</Badge>
                                    <Badge background="rgba(248, 200, 96, 0.18)" color="#f8c860">{analyticsItemTypeLabel}</Badge>
                                </ChipRow>
                            </>
                        ) : (
                            <>
                                <MonthHeading>
                                    <NavArrow type="button" onClick={() => navigateCalendar(-1)}>‹</NavArrow>
                                    <div>{calendarHeading}</div>
                                    <NavArrow type="button" onClick={() => navigateCalendar(1)}>›</NavArrow>
                                    <TodayButton type="button" onClick={() => { setCursor(timezoneToday); setSelectedDate(timezoneToday); }}>Jump to Today</TodayButton>
                                </MonthHeading>
                                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                                    <ViewTabs>
                                        {VIEW_OPTIONS.map(view => (
                                            <ViewTab key={view} type="button" active={activeView === view} onClick={() => { setActiveView(view); setCursor(selectedDate); }}>
                                                {view}
                                            </ViewTab>
                                        ))}
                                    </ViewTabs>
                                    <PrimaryAction type="button" onClick={() => openNewCalendarItemModal()}>+ New</PrimaryAction>
                                </div>
                            </>
                        )}
                    </CalendarHeader>
                    {analyticsOpen ? (
                        <AnalyticsShell>
                            {analyticsSummary.totalItems === 0 && !analyticsIncludesToday ? (
                                <EmptyStateCard>
                                    <TodoHeading>No analytics data</TodoHeading>
                                    <SmallText>No calendar items matched the current period and filter combination.</SmallText>
                                </EmptyStateCard>
                            ) : (
                                <>
                                    <AnalyticsHero>
                                        <CardTitleRow>
                                            <div>
                                                <TodoHeading>{analyticsPeriod} outcome overview</TodoHeading>
                                                <SmallText>A simple summary of how this {analyticsPeriod.toLowerCase()} is going, including completed items, focus time, and overdue items.</SmallText>
                                            </div>
                                        </CardTitleRow>
                                        <AnalyticsHeroGrid>
                                            <AnalyticsCard span={12} style={{ padding: 0, border: "none", background: "transparent", minHeight: "auto" }}>
                                                <MiniStatGrid>
                                                    <MiniStat>
                                                        <SectionLabel>{analyticsPeriod} Outcome Score</SectionLabel>
                                                        <MetricValue>{analyticsSummary.outcomeScore}</MetricValue>
                                                        <SmallText>Higher is better. This score goes up when work gets finished and focus time is protected, and down when items become overdue.</SmallText>
                                                    </MiniStat>
                                                    <MiniStat>
                                                        <SectionLabel>Completion vs Intention</SectionLabel>
                                                        <MetricValue>{analyticsSummary.completionRate}%</MetricValue>
                                                        <SmallText>{analyticsSummary.completedTasks} of {analyticsTasks.length || 0} planned task items in this view have been marked complete.</SmallText>
                                                    </MiniStat>
                                                    <MiniStat title="Calendar items that are incomplete and have passed their planned end.">
                                                        <SectionLabel>Overdue Items</SectionLabel>
                                                        <MetricValue>{analyticsSummary.overdueItems}</MetricValue>
                                                        <SmallText>These items are past their planned end date and still not complete. Lower is better.</SmallText>
                                                    </MiniStat>
                                                </MiniStatGrid>
                                            </AnalyticsCard>
                                            <AnalyticsCard span={12}>
                                                <CardTitleRow>
                                                    <div>
                                                        <SectionLabel>Activity Trend</SectionLabel>
                                                        <SmallText>Each stacked bar shows the actual item type mix in this selected {analyticsPeriod.toLowerCase()} view.</SmallText>
                                                    </div>
                                                    <ChipRow>
                                                        {analyticsSummary.byType.map(item => (
                                                            <Badge
                                                                key={`trend-legend-${item.key}`}
                                                                background={withAlpha(item.color, "24")}
                                                                color={item.color}
                                                            >
                                                                {item.label}
                                                            </Badge>
                                                        ))}
                                                    </ChipRow>
                                                </CardTitleRow>
                                                <TrendBars count={analyticsSummary.trend.length}>
                                                    {analyticsSummary.trend.map((point, index) => {
                                                        const barHeight = getAnalyticsBarHeight(point.total, analyticsSummary.trendMaxTotal);
                                                        const pointSummary = point.breakdown
                                                            .filter(item => item.count > 0)
                                                            .map(item => `${item.label}: ${item.count}`)
                                                            .join(", ");
                                                        return (
                                                            <TrendColumn key={point.label} title={`${point.title}: ${point.total} items${pointSummary ? ` (${pointSummary})` : ""}`}>
                                                                <TrendStack>
                                                                    <TrendTrack>
                                                                        <TrendBar height={barHeight} hasValue={point.total > 0} $ready={analyticsChartsReady} $delay={`${Math.min(0.36, 0.04 * point.breakdown.length + 0.03 * index)}s`}>
                                                                            {point.breakdown.filter(item => item.count > 0).map(item => (
                                                                                <TrendBarSegment
                                                                                    key={`${point.label}-${item.key}`}
                                                                                    flex={item.count}
                                                                                    minHeight={item.count > 0 ? 8 : 0}
                                                                                    background={item.gradient}
                                                                                />
                                                                            ))}
                                                                        </TrendBar>
                                                                    </TrendTrack>
                                                                </TrendStack>
                                                                <ChartCaption>{point.label}</ChartCaption>
                                                            </TrendColumn>
                                                        );
                                                    })}
                                                </TrendBars>
                                            </AnalyticsCard>
                                        </AnalyticsHeroGrid>
                                    </AnalyticsHero>
                                    <AnalyticsGrid>
                                        <AnalyticsCard span={4}>
                                            <CardTitleRow>
                                                <div>
                                                    <SectionLabel>Task Time</SectionLabel>
                                                    <SmallText>Time represented by task-style calendar items from the current item type data.</SmallText>
                                                </div>
                                            </CardTitleRow>
                                            <DonutWrap>
                                                <DonutSvg viewBox="0 0 140 140" aria-hidden="true">
                                                    <defs>
                                                        <linearGradient id="analyticsFocusRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                            <stop offset="0%" stopColor="#42e7b6" />
                                                            <stop offset="55%" stopColor="#10b981" />
                                                            <stop offset="100%" stopColor="#0f766e" />
                                                        </linearGradient>
                                                    </defs>
                                                    <DonutTrack cx="70" cy="70" r="58" />
                                                    <DonutProgress
                                                        cx="70"
                                                        cy="70"
                                                        r="58"
                                                        $circumference={analyticsFocusCircumference}
                                                        $offset={analyticsFocusOffset}
                                                        $ready={analyticsChartsReady}
                                                        $delay="0.08s"
                                                    />
                                                </DonutSvg>
                                                <DonutCenter>
                                                    <div>
                                                        <MetricValue style={{ fontSize: "1.4rem" }}>{analyticsSummary.focusHours}h</MetricValue>
                                                        <SmallText>Task time</SmallText>
                                                    </div>
                                                </DonutCenter>
                                            </DonutWrap>
                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                                <SmallText>Planned task time: {analyticsSummary.plannedFocusHours}h</SmallText>
                                                <SmallText>Reached: {analyticsSummary.achievedFocusPercent}%</SmallText>
                                            </div>
                                        </AnalyticsCard>
                                        <AnalyticsCard span={4}>
                                            <CardTitleRow>
                                                <div>
                                                    <SectionLabel>Scheduled Items vs Completed Work</SectionLabel>
                                                    <SmallText>This compares scheduled calendar items against task items that were marked complete.</SmallText>
                                                </div>
                                            </CardTitleRow>
                                            <MetricBars count={analyticsSummary.meetingVsCompleted.length} $compact>
                                                {analyticsSummary.meetingVsCompleted.map((item, index) => (
                                                    <TrendColumn key={item.key} title={`${item.label}: ${item.count} items`}>
                                                        <TrendStack>
                                                            <TrendTrack $compact>
                                                                <TrendBar
                                                                    height={getAnalyticsBarHeight(item.count, analyticsSummary.meetingVsCompletedMax)}
                                                                    hasValue={item.count > 0}
                                                                    $ready={analyticsChartsReady}
                                                                    $delay={`${0.08 + (index * 0.06)}s`}
                                                                    $compact
                                                                >
                                                                    <TrendBarSegment flex={item.count} minHeight={item.count > 0 ? 18 : 0} background={item.gradient} />
                                                                </TrendBar>
                                                            </TrendTrack>
                                                        </TrendStack>
                                                        <ChartCaption>{item.label}</ChartCaption>
                                                        <strong>{item.count}</strong>
                                                        <ChartCaption>{item.note}</ChartCaption>
                                                    </TrendColumn>
                                                ))}
                                            </MetricBars>
                                            <div style={{ display: "grid", gap: 8 }}>
                                                <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Scheduled items</SmallText><strong>{analyticsSummary.meetingLoad}</strong></div>
                                                <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Completed work items</SmallText><strong>{analyticsSummary.productiveTasks}</strong></div>
                                                <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Scheduled hours</SmallText><strong>{analyticsSummary.meetingHours}h</strong></div>
                                                <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Work hours</SmallText><strong>{analyticsSummary.taskHours}h</strong></div>
                                            </div>
                                        </AnalyticsCard>
                                        <AnalyticsCard span={4}>
                                            <CardTitleRow>
                                                <div>
                                                    <SectionLabel>Item Type Breakdown</SectionLabel>
                                                    <SmallText>This shows which API item types make up this view.</SmallText>
                                                </div>
                                            </CardTitleRow>
                                            <MetricBars count={analyticsSummary.byType.length} $stackOnMobile $compact>
                                                {analyticsSummary.byType.map((item, index) => (
                                                    <TrendColumn key={item.key} title={`${item.label}: ${item.count} calendar items`} $horizontalOnMobile>
                                                        <ChartCaption>{item.label}</ChartCaption>
                                                        <TrendStack $horizontalOnMobile>
                                                            <TrendTrack $horizontalOnMobile $compact>
                                                                <TrendBar
                                                                    height={getAnalyticsBarHeight(item.count, analyticsSummary.typeBreakdownMax)}
                                                                    hasValue={item.count > 0}
                                                                    $ready={analyticsChartsReady}
                                                                    $delay={`${0.1 + (index * 0.05)}s`}
                                                                    $horizontalOnMobile
                                                                    $compact
                                                                >
                                                                    <TrendBarSegment flex={item.count} minHeight={item.count > 0 ? 18 : 0} background={item.gradient} />
                                                                </TrendBar>
                                                            </TrendTrack>
                                                        </TrendStack>
                                                        <strong>{item.count}</strong>
                                                    </TrendColumn>
                                                ))}
                                            </MetricBars>
                                        </AnalyticsCard>
                                        <AnalyticsCard span={6}>
                                            <CardTitleRow>
                                                <div>
                                                    <SectionLabel>Where Items Came From</SectionLabel>
                                                    <SmallText>These tags show the real sources behind the items in this view, including manual entries, automations, and connected calendars.</SmallText>
                                                </div>
                                            </CardTitleRow>
                                            <SourceChipRow>
                                                {analyticsSummary.bySource.length > 0 ? analyticsSummary.bySource.map(item => (
                                                    <SourceChip key={item.key} background={withAlpha(item.color, "24")} color={item.color} title={`${item.label}: ${item.count} tracked calendar items`}>
                                                        {item.label} · {item.count}
                                                    </SourceChip>
                                                )) : <SmallText>No source data matched the current filter.</SmallText>}
                                            </SourceChipRow>
                                        </AnalyticsCard>
                                        <AnalyticsCard span={6}>
                                            <CardTitleRow>
                                                <div>
                                                    <SectionLabel>What This View Suggests</SectionLabel>
                                                    <SmallText>These quick signals help explain whether this period looks steady, overloaded, or well balanced.</SmallText>
                                                </div>
                                            </CardTitleRow>
                                            <MiniStatGrid>
                                                <MiniStat>
                                                    <SectionLabel>Productivity Streak</SectionLabel>
                                                    <MetricValue>{analyticsSummary.productivityStreak}</MetricValue>
                                                    <SmallText>How many periods in a row have shown steady completed work.</SmallText>
                                                </MiniStat>
                                                <MiniStat>
                                                    <SectionLabel>Burnout Indicator</SectionLabel>
                                                    <MetricValue style={{ fontSize: "1.35rem" }}>{analyticsSummary.burnoutRisk}</MetricValue>
                                                    <SmallText>A simple warning based on whether scheduled time is taking over time that could have been used for task work.</SmallText>
                                                </MiniStat>
                                                <MiniStat>
                                                    <SectionLabel>Personal vs Shared</SectionLabel>
                                                    <MetricValue style={{ fontSize: "1.35rem" }}>{analyticsSummary.visibilitySplit.personal}:{analyticsSummary.visibilitySplit.shared}</MetricValue>
                                                    <SmallText>The number on the left is personal items; the number on the right is shared items for {getCustomGroupNameLower("employees")} or {getCustomGroupNameLower("clients")}.</SmallText>
                                                </MiniStat>
                                            </MiniStatGrid>
                                            <StatBox style={{ marginTop: 0 }}>
                                                <SectionLabel>Smart Recommendation</SectionLabel>
                                                <SmallText>This is the main suggestion based on your current mix of scheduled time, task time, and overdue work: {analyticsSummary.recommendation}</SmallText>
                                            </StatBox>
                                        </AnalyticsCard>
                                    </AnalyticsGrid>
                                </>
                            )}
                        </AnalyticsShell>
                    ) : activeView === "Month" && (
                        <>
                            <WeekdaysRow>
                                {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map(day => (
                                    <WeekdayHeader key={day} weekend={day === "SAT" || day === "SUN"}>
                                        {day}
                                    </WeekdayHeader>
                                ))}
                            </WeekdaysRow>
                            <CalendarGridWrap>
                                {monthWeeks.map((week, weekIndex) => {
                                    const lanes = buildWeekSpanLanes(week, monthSpanItems, timezone);
                                    const barLanes = lanes.length;
                                    const maxDayItemsInWeek = Math.max(0, ...week.map((day) => {
                                        const dayScheduledItems = visibleScheduledItems.filter(item => isSurveyOrPollAutomationItem(item)
                                            ? automationEndpointOverlapsDate(item, day, timezone)
                                            : scheduledItemOverlapsDate(item, day, timezone) && isSingleDayEvent(item));
                                        const dayTasks = visibleTasks.filter(task => isSurveyOrPollAutomationItem(task)
                                            ? automationEndpointOverlapsDate(task, day, timezone)
                                            : taskOverlapsDate(task, day) && !isMultiDayTask(task));
                                        return dayScheduledItems.length + dayTasks.length;
                                    }));
                                    const rowHeight = getMonthWeekRowHeight({ barLanes, maxDayItemsInWeek });
                                    const itemOffset = getMonthWeekItemsOffset(barLanes);
                                    const maxVisibleItemsForWeek = maxDayItemsInWeek;
                                    return (
                                        <MonthWeekRow key={`week-${weekIndex}`} rowHeight={rowHeight}>
                                            {barLanes > 0 && (
                                                <MonthSpanLayer>
                                                    {lanes.map((lane, laneIndex) => (
                                                        <MonthSpanRow key={`lane-${laneIndex}`}>
                                                            {lane.map(({ item, colStart, colEnd, startsInWeek, endsInWeek }) => {
                                                                const pillColors = getCalendarPillColors(getCalendarItemDisplayColor(item));
                                                                return (
                                                                    <MonthSpanItem
                                                                        key={item.id}
                                                                        type="button"
                                                                        background={pillColors.background}
                                                                        color={pillColors.text}
                                                                        style={{ gridColumn: `${colStart} / ${colEnd}` }}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            openDetail(item);
                                                                        }}
                                                                    >
                                                                        <MonthSpanLead>
                                                                            <MonthSpanBullet color={pillColors.dot} />
                                                                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                {item.title}
                                                                            </span>
                                                                        </MonthSpanLead>
                                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                            {endsInWeek
                                                                                ? hasScheduledCalendarFields(item)
                                                                                    ? `ends ${formatScheduledItemTime(item, timezone).split(" - ")[1] || ""}`.trim()
                                                                                    : (item.endTime ? `ends ${formatTaskDueTime({ dueDate: item.endDate || item.dueDate, dueTime: item.endTime }, timezone)}` : "")
                                                                                : ""}
                                                                        </span>
                                                                    </MonthSpanItem>
                                                                );
                                                            })}
                                                        </MonthSpanRow>
                                                    ))}
                                                </MonthSpanLayer>
                                            )}
                                            <MonthWeekCells barLanes={barLanes} rowHeight={rowHeight}>
                                                {week.map((day, dayIndex) => {
                                                    const dayScheduledItems = visibleScheduledItems.filter(item => isSurveyOrPollAutomationItem(item)
                                                        ? automationEndpointOverlapsDate(item, day, timezone)
                                                        : scheduledItemOverlapsDate(item, day, timezone) && isSingleDayEvent(item));
                                                    const dayTasks = visibleTasks.filter(task => isSurveyOrPollAutomationItem(task)
                                                        ? automationEndpointOverlapsDate(task, day, timezone)
                                                        : taskOverlapsDate(task, day) && !isMultiDayTask(task));
                                                    const dayItems = [...dayScheduledItems, ...dayTasks]
                                                        .sort((left, right) => getItemStartValue(left, timezone) - getItemStartValue(right, timezone));
                                                    const cells = dayItems.slice(0, maxVisibleItemsForWeek);
                                                    const overflow = Math.max(0, dayItems.length - cells.length);
                                                    const holidayLabels = showPublicHolidays ? (publicHolidayMap[day.format("YYYY-MM-DD")] || []) : [];
                                                    return (
                                                        <DayCell
                                                            key={day.format("YYYY-MM-DD")}
                                                            type="button"
                                                            active={day.isSame(selectedDate, "day")}
                                                            rowHeight={rowHeight}
                                                            onClick={() => { setSelectedDate(day); setCursor(day); }}
                                                            title={holidayLabels.length ? holidayLabels.join(" · ") : undefined}
                                                        >
                                                            <DayNumber today={day.isSame(timezoneToday, "day")} muted={!day.isSame(cursor, "month")}>{day.format("D")}</DayNumber>
                                                            <DayCellItems offset={itemOffset}>
                                                                {cells.map(item => {
                                                                    const pillColors = getCalendarPillColors(getCalendarItemDisplayColor(item));
                                                                    return (
                                                                        <ItemPill
                                                                            key={item.id}
                                                                            type="button"
                                                                            background={pillColors.background}
                                                                            color={pillColors.text}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                openDetail(item);
                                                                            }}
                                                                        >
                                                                            <ItemBullet color={pillColors.dot} />
                                                                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                {isSurveyOrPollAutomationItem(item)
                                                                                    ? getAutomationEndpointTitle(item, day, "day", timezone)
                                                                                    : item.title}
                                                                            </span>
                                                                        </ItemPill>
                                                                    );
                                                                })}
                                                                {overflow > 0 && <MoreText>+{overflow} {overflow === 1 ? "item" : "items"}</MoreText>}
                                                            </DayCellItems>
                                                            {holidayLabels.length > 0 && (
                                                                <DayCellFooter>
                                                                    <StaticCellLabel muted={!day.isSame(cursor, "month")}>
                                                                        {getHolidayFooterLabel(holidayLabels)}
                                                                    </StaticCellLabel>
                                                                </DayCellFooter>
                                                            )}
                                                        </DayCell>
                                                    );
                                                })}
                                            </MonthWeekCells>
                                        </MonthWeekRow>
                                    );
                                })}
                            </CalendarGridWrap>
                        </>
                    )}
                    {!analyticsOpen && activeView === "Day" && (
                        <DayAgenda>
                            {HOUR_LABELS.map(({ hour, label }) => {
                                const hourScheduledItems = selectedDateTimedScheduledItems.filter(item => scheduledItemTouchesHour(item, selectedDate, hour, timezone));
                                const hourTasks = tasksForSelectedDate.filter(task => Number((task.dueTime || "").split(":")[0]) === hour);
                                const hourItems = [...hourScheduledItems, ...hourTasks];
                                return (
                                    <React.Fragment key={hour}>
                                        <DayHourLabel>{label}</DayHourLabel>
                                        <DayHourRow active={timezoneToday.isSame(selectedDate, "day") && timezoneToday.hour() === hour}>
                                            {selectedDateAllDayScheduledItems.length > 0 && hour === 0 && selectedDateAllDayScheduledItems.map(item => {
                                                const pillColors = getCalendarPillColors(getCalendarItemDisplayColor(item));
                                                return (
                                                    <ItemPill key={item.id} type="button" background={pillColors.background} color={pillColors.text} onClick={() => openDetail(item)}>
                                                        <ItemBullet color={pillColors.dot} />
                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</span>
                                                    </ItemPill>
                                                );
                                            })}
                                            {hourItems.map(item => {
                                                const pillColors = getCalendarPillColors(getCalendarItemDisplayColor(item));
                                                return (
                                                    <ItemPill key={item.id} type="button" background={pillColors.background} color={pillColors.text} onClick={() => openDetail(item)}>
                                                        <ItemBullet color={pillColors.dot} />
                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</span>
                                                    </ItemPill>
                                                );
                                            })}
                                        </DayHourRow>
                                    </React.Fragment>
                                );
                            })}
                        </DayAgenda>
                    )}
                    {!analyticsOpen && activeView === "Week" && (
                        <WeekGrid>
                            <WeekHourCorner />
                            {weekDays.map(day => (
                                <WeekHeaderCell key={day.format("YYYY-MM-DD")} type="button" active={day.isSame(selectedDate, "day")} onClick={() => { setSelectedDate(day); setCursor(day); }}>
                                    <WeekHeaderTitle>{day.format("ddd").toUpperCase()}</WeekHeaderTitle>
                                    <WeekHeaderDate today={day.isSame(timezoneToday, "day")}>{day.format("D")}</WeekHeaderDate>
                                </WeekHeaderCell>
                            ))}
                            <WeekAllDayLabel height={weekAllDayRowHeight}>All day</WeekAllDayLabel>
                            <WeekAllDayArea height={weekAllDayRowHeight}>
                                <WeekAllDayLayer>
                                    {weekSpanLanes.map((lane, laneIndex) => (
                                        <WeekAllDayRow key={`week-span-lane-${laneIndex}`}>
                                            {lane.map(({ item, colStart, colEnd, startsInWeek, endsInWeek }) => {
                                                const pillColors = getCalendarPillColors(getCalendarItemDisplayColor(item));
                                                return (
                                                    <MonthSpanItem
                                                        key={item.id}
                                                        type="button"
                                                        background={pillColors.background}
                                                        color={pillColors.text}
                                                        style={{ gridColumn: `${colStart} / ${colEnd}` }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openDetail(item);
                                                        }}
                                                    >
                                                        <MonthSpanLead>
                                                            <MonthSpanBullet color={pillColors.dot} />
                                                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {item.title}
                                                            </span>
                                                        </MonthSpanLead>
                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {hasScheduledCalendarFields(item)
                                                                ? (startsInWeek || !item.allDay ? formatScheduledItemTime(item, timezone) : "")
                                                                : (startsInWeek ? formatTaskDueTime({ dueDate: item.startDate || item.dueDate, dueTime: item.startTime || item.dueTime, allDay: item.allDay }, timezone) : "")}
                                                        </span>
                                                    </MonthSpanItem>
                                                );
                                            })}
                                        </WeekAllDayRow>
                                    ))}
                                </WeekAllDayLayer>
                            </WeekAllDayArea>
                            {HOUR_LABELS.map(({ hour, label }) => (
                                <React.Fragment key={hour}>
                                    <WeekHourLabel>{label}</WeekHourLabel>
                                    {weekDays.map(day => {
                                        const slotScheduledItems = visibleScheduledItems.filter(item => isSurveyOrPollAutomationItem(item)
                                            ? automationEndpointTouchesHour(item, day, hour, timezone)
                                            : scheduledItemOverlapsDate(item, day, timezone) && scheduledItemTouchesHour(item, day, hour, timezone));
                                        const slotTasks = visibleTasks.filter(task => isSurveyOrPollAutomationItem(task)
                                            ? automationEndpointTouchesHour(task, day, hour, timezone)
                                            : task.dueDate === day.format("YYYY-MM-DD") && Number((task.dueTime || "").split(":")[0]) === hour);
                                        const allSlotItems = [...slotScheduledItems, ...slotTasks];
                                        const slotItems = allSlotItems.slice(0, 2);
                                        const overflow = Math.max(0, allSlotItems.length - slotItems.length);
                                        return (
                                            <WeekSlot key={`${day.format("YYYY-MM-DD")}-${hour}`} type="button" active={day.isSame(selectedDate, "day")} onClick={() => { setSelectedDate(day); setCursor(day); }}>
                                                {slotItems.map(item => {
                                                    const pillColors = getCalendarPillColors(getCalendarItemDisplayColor(item));
                                                    return (
                                                        <ItemPill key={item.id} type="button" background={pillColors.background} color={pillColors.text} onClick={(event) => { event.stopPropagation(); openDetail(item); }}>
                                                            <ItemBullet color={pillColors.dot} />
                                                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {isSurveyOrPollAutomationItem(item)
                                                                    ? getAutomationEndpointTitle(item, day.startOf("day").hour(hour), "hour", timezone)
                                                                    : item.title}
                                                            </span>
                                                        </ItemPill>
                                                    );
                                                })}
                                                {overflow > 0 && <MoreText>+{overflow} {overflow === 1 ? "item" : "items"}</MoreText>}
                                            </WeekSlot>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </WeekGrid>
                    )}
                    {!analyticsOpen && activeView === "Year" && (
                        <YearGrid>
                            {yearMonths.map(month => {
                                const days = monthMatrix(month).slice(0, 42);
                                return (
                                    <YearMonthCard key={month.format("YYYY-MM")}>
                                        <YearMonthHeader>{month.format("MMMM")}</YearMonthHeader>
                                        <YearMonthGrid>
                                            {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => <YearWeekday key={`${month.format("MM")}-${day}-${index}`}>{day}</YearWeekday>)}
                                            {days.map(day => {
                                                const scheduledItems = visibleScheduledItems.filter(item => isSurveyOrPollAutomationItem(item)
                                                    ? automationEndpointOverlapsDate(item, day, timezone)
                                                    : scheduledItemOverlapsDate(item, day, timezone));
                                                const taskItems = visibleTasks.filter(item => isSurveyOrPollAutomationItem(item) && automationEndpointOverlapsDate(item, day, timezone));
                                                const items = [...scheduledItems, ...taskItems];
                                                return (
                                                    <YearDayCell
                                                        key={day.format("YYYY-MM-DD")}
                                                        type="button"
                                                        active={day.isSame(selectedDate, "day")}
                                                        muted={!day.isSame(month, "month")}
                                                        onClick={() => { setSelectedDate(day); setCursor(day); setActiveView("Month"); }}
                                                    >
                                                        {day.format("D")}
                                                        {items[0] && <YearDayDot color={getCalendarItemDisplayColor(items[0])} />}
                                                    </YearDayCell>
                                                );
                                            })}
                                        </YearMonthGrid>
                                    </YearMonthCard>
                                );
                            })}
                        </YearGrid>
                    )}
                </CenterPanel>

                <RightPanel $collapsed={rightPanelCollapsed}>
                    {rightPanelCollapsed ? (
                        <CollapsedPanelButton
                            $side="right"
                            type="button"
                            aria-label="Expand actions panel"
                            aria-expanded={!rightPanelCollapsed}
                            title="Expand actions panel"
                            onClick={() => setRightPanelCollapsed(false)}
                        >
                            <PanelCollapseGlyph aria-hidden="true">‹</PanelCollapseGlyph>
                            <CollapsedPanelLabel $side="right">Actions</CollapsedPanelLabel>
                        </CollapsedPanelButton>
                    ) : (
                        <>
                            <PanelHeaderRow $framed>
                                <PanelHeaderTitle>Actions</PanelHeaderTitle>
                                <PanelCollapseButton
                                    type="button"
                                    aria-label="Collapse actions panel"
                                    aria-expanded={!rightPanelCollapsed}
                                    title="Collapse actions panel"
                                    onClick={() => setRightPanelCollapsed(true)}
                                >
                                    <PanelCollapseGlyph aria-hidden="true">›</PanelCollapseGlyph>
                                </PanelCollapseButton>
                            </PanelHeaderRow>
                            {analyticsOpen ? (
                                <RightPanelInner>
                                    <div>
                                        <TodoHeading>Analytics Snapshot</TodoHeading>
                                        <SmallText>{analyticsPeriod} reporting for {getAnalyticsVisibilityLabel(analyticsVisibility).toLowerCase()} calendar items.</SmallText>
                                    </div>
                                    <StatBox>
                                        <SectionLabel>Outcome Snapshot</SectionLabel>
                                        <div style={{ display: "grid", gap: 10 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>{analyticsPeriod} Outcome Score</SmallText><strong>{analyticsSummary.outcomeScore}</strong></div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Completion rate</SmallText><strong>{analyticsSummary.completionRate}%</strong></div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Completed task items</SmallText><strong>{analyticsSummary.completedTasks}</strong></div>
                                        </div>
                                    </StatBox>
                                    <StatBox>
                                        <SectionLabel>Risk + Streak</SectionLabel>
                                        <div style={{ display: "grid", gap: 8 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Burnout indicator</SmallText><strong>{analyticsSummary.burnoutRisk}</strong></div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Productivity streak</SmallText><strong>{analyticsSummary.productivityStreak}</strong></div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Overdue calendar items</SmallText><strong>{analyticsSummary.overdueItems}</strong></div>
                                        </div>
                                    </StatBox>
                                    <div style={{ display: "grid", gap: "0.7rem" }}>
                                        <SectionLabel>Suggested Next Steps</SectionLabel>
                                        {analyticsRecommendations.map((recommendation) => (
                                            <SuggestionFeedCard key={recommendation.id}>
                                                <SuggestionFeedCardHeader>
                                                    <SuggestionIndicator background={recommendation.indicatorBackground} color={recommendation.indicatorColor}>
                                                        {recommendation.icon}
                                                    </SuggestionIndicator>
                                                    <SuggestionFeedBody>
                                                        <SuggestionFeedTitle>{recommendation.text}</SuggestionFeedTitle>
                                                        <SuggestionFeedMeta>{recommendation.meta}</SuggestionFeedMeta>
                                                    </SuggestionFeedBody>
                                                </SuggestionFeedCardHeader>
                                            </SuggestionFeedCard>
                                        ))}
                                    </div>
                                </RightPanelInner>
                            ) : (
                                <>
                                    <RightTabs>
                                        {VISIBLE_RIGHT_TABS.map(tab => (
                                            <RightTab key={tab} type="button" active={activeRightTab === tab} onClick={() => setActiveRightTab(tab)}>
                                                {tab}
                                            </RightTab>
                                        ))}
                                    </RightTabs>

                                    <RightPanelInner>
                                        {activeRightTab === "To-Do" && (
                                            <>
                                                <div>
                                                    <TodoHeading>{selectedDateLabel}</TodoHeading>
                                                    <SmallText>{todaysTasksProgress.total} tasks · {todaysTasksProgress.done} completed</SmallText>
                                                </div>
                                                <ProgressWrap>
                                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                                                        <span>Daily progress</span>
                                                        <span style={{ color: "#4de0af", fontWeight: 800 }}>{todaysTasksProgress.percent}%</span>
                                                    </div>
                                                    <ProgressBar><ProgressFill percent={todaysTasksProgress.percent} /></ProgressBar>
                                                </ProgressWrap>
                                                <div>
                                                    <WeatherHeadline>
                                                        <TodoHeading style={{ fontSize: "0.95rem" }}>Weather</TodoHeading>
                                                        <SmallText>{weatherLocationLabel}</SmallText>
                                                    </WeatherHeadline>
                                                    {weatherStatus === "unavailable" ? (
                                                        <WeatherStrip style={{ marginTop: 10 }}>
                                                            <WeatherUnavailableCard>
                                                                <div style={{ fontWeight: 700 }}>Weather Unavailable</div>
                                                                <SmallText>Forecast data could not be loaded for this location right now.</SmallText>
                                                            </WeatherUnavailableCard>
                                                        </WeatherStrip>
                                                    ) : (
                                                        <WeatherStrip style={{ marginTop: 10 }}>
                                                            {(weatherInfo || Array.from({ length: 5 })).map((day, index) => (
                                                                <WeatherCard key={day?.label || index}>
                                                                    <div style={{ fontSize: 10, color: "var(--calendar-text-soft)" }}>{day?.label || "..."}</div>
                                                                    <div style={{ fontSize: 18 }}>{day?.icon || "🌤️"}</div>
                                                                    <div style={{ fontWeight: 700 }}>
                                                                        {Number.isFinite(day?.max) ? `${day.max}°` : "--"}
                                                                    </div>
                                                                </WeatherCard>
                                                            ))}
                                                        </WeatherStrip>
                                                    )}
                                                </div>
                                                <div>
                                                    <div>
                                                        <TodoHeading style={{ fontSize: "0.95rem" }}>Calendar Items</TodoHeading>
                                                        <SmallText>{selectedDatePanelItems.length} scheduled</SmallText>
                                                    </div>
                                                </div>
                                                {selectedDatePanelItems.length > 0 ? (
                                                    <TaskList>
                                                        {selectedDatePanelItems.map(item => {
                                                            const isActionItem = hasActionCalendarFields(item);
                                                            const isScheduledItem = hasScheduledCalendarFields(item);
                                                            const hasManualItemType = isManualActionCalendarItem(item);
                                                            const metaLabel = isActionItem
                                                                ? getItemTypeLabel(item.itemTypeUuid || item.taskType, managedItemTypes)
                                                                : getCalendarItemSourceLabel(item.source, "Calendar Item");
                                                            const timeLabel = isScheduledItem
                                                                ? formatScheduledItemTime(item, timezone)
                                                                : formatTaskDueTime(item, timezone);
                                                            return (
                                                                <TaskCard key={`${getCalendarItemMode(item)}-${item.id}`}>
                                                                    {hasManualItemType ? (
                                                                        <TaskCheck type="button" checked={item.completed} onClick={() => toggleTaskDone(item)}>
                                                                            {item.completed && <TaskCheckIcon src={GreenCheckIcon} alt="" aria-hidden="true" />}
                                                                        </TaskCheck>
                                                                    ) : (
                                                                        <TaskCardIndicator background={`${item.color}22`} color={item.color}>•</TaskCardIndicator>
                                                                    )}
                                                                    <div>
                                                                        <TaskTitle done={hasManualItemType && item.completed}>{item.title}</TaskTitle>
                                                                        <TaskMeta>
                                                                            <TaskTimeText>{timeLabel}</TaskTimeText>
                                                                            {metaLabel && <Badge background={`${item.color}22`} color={item.color}>{metaLabel}</Badge>}
                                                                            {isActionItem && item.priority && (
                                                                                <Badge background={`${priorityTone[item.priority]}22`} color={priorityTone[item.priority]}>{priorityLabel[item.priority]}</Badge>
                                                                            )}
                                                                        </TaskMeta>
                                                                    </div>
                                                                    <IconButton type="button" onClick={() => openDetail(item)}>›</IconButton>
                                                                </TaskCard>
                                                            );
                                                        })}
                                                    </TaskList>
                                                ) : (
                                                    <SmallText>No calendar items for this date.</SmallText>
                                                )}
                                                <SuggestionsFeedSection>
                                                    <SuggestionsFeedHeader>
                                                        <div>
                                                            <TodoHeading style={{ fontSize: "0.95rem" }}>Suggestions Feed</TodoHeading>
                                                            <SmallText>Smart, actionable suggestions based on your current calendar patterns.</SmallText>
                                                        </div>
                                                        <SuggestionsRefreshButton type="button" onClick={() => setSuggestionRefreshTick(prev => prev + 1)}>
                                                            Refresh
                                                        </SuggestionsRefreshButton>
                                                    </SuggestionsFeedHeader>
                                                    {visibleSuggestions.length > 0 ? (
                                                        <SuggestionsFeedCards>
                                                            {visibleSuggestions.map((suggestion) => (
                                                                <SuggestionFeedCard key={suggestion.id}>
                                                                    <SuggestionFeedCardHeader>
                                                                        <SuggestionIndicator background={suggestion.indicatorBackground} color={suggestion.indicatorColor}>
                                                                            {suggestion.icon}
                                                                        </SuggestionIndicator>
                                                                        <SuggestionFeedBody>
                                                                            <SuggestionFeedTitle>{suggestion.text}</SuggestionFeedTitle>
                                                                            {suggestion.meta && <SuggestionFeedMeta>{suggestion.meta}</SuggestionFeedMeta>}
                                                                        </SuggestionFeedBody>
                                                                        <SuggestionDismissButton type="button" onClick={() => handleSuggestionDismiss(suggestion.id)}>
                                                                            ×
                                                                        </SuggestionDismissButton>
                                                                    </SuggestionFeedCardHeader>
                                                                    <SuggestionFeedActions>
                                                                        <SuggestionActionButton type="button" onClick={() => handleSuggestionAction(suggestion)}>
                                                                            {suggestion.actionLabel}
                                                                        </SuggestionActionButton>
                                                                    </SuggestionFeedActions>
                                                                </SuggestionFeedCard>
                                                            ))}
                                                        </SuggestionsFeedCards>
                                                    ) : (visibleTasks.length === 0 && visibleScheduledItems.length === 0) ? (
                                                        <EmptyStateCard style={{ minHeight: 132 }}>
                                                            <TodoHeading style={{ fontSize: "0.95rem" }}>No relevant data yet</TodoHeading>
                                                            <SmallText>Add a few calendar items and this feed will start surfacing smarter suggestions.</SmallText>
                                                        </EmptyStateCard>
                                                    ) : (
                                                        <EmptyStateCard style={{ minHeight: 132 }}>
                                                            <TodoHeading style={{ fontSize: "0.95rem" }}>You are in a good place</TodoHeading>
                                                            <SmallText>No strong planning suggestions right now. Refresh the feed after your schedule changes.</SmallText>
                                                        </EmptyStateCard>
                                                    )}
                                                </SuggestionsFeedSection>
                                            </>
                                        )}

                                        {activeRightTab === "Meetings" && (
                                            <>
                                                <TodoHeading>📬 Meeting Requests</TodoHeading>
                                                <EmptyStateCard style={{ minHeight: 132 }}>
                                                    <TodoHeading style={{ fontSize: "0.95rem" }}>No meeting requests</TodoHeading>
                                                    <SmallText>Meeting requests will appear here when they are available from the Calendar API.</SmallText>
                                                </EmptyStateCard>
                                            </>
                                        )}

                                        {activeRightTab === "Smart" && (
                                            <>
                                                <div>
                                                    <TodoHeading>✨ Smart Features</TodoHeading>
                                                    <SmallText>Unique to Me Business</SmallText>
                                                </div>
                                                {smartFeatures.map(feature => (
                                                    <FeatureCard key={feature.title}>
                                                        <FeatureIcon color={feature.tint}>{feature.icon}</FeatureIcon>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                                                <div style={{ fontWeight: 700 }}>{feature.title}</div>
                                                                {feature.ai && <Badge background="rgba(109,116,255,0.18)" color="var(--calendar-accent)">✦ AI</Badge>}
                                                            </div>
                                                            <SmallText>{feature.text}</SmallText>
                                                        </div>
                                                    </FeatureCard>
                                                ))}
                                            </>
                                        )}

                                        {activeRightTab === "Today" && (
                                            <>
                                                <div>
                                                    <TodoHeading>⚡ Today — {selectedDate.format("ddd D MMM")}</TodoHeading>
                                                </div>
                                                {upcomingItems.map(item => (
                                                    <TodayItem key={item.id}>
                                                        <div>
                                                            <div style={{ fontWeight: 800 }}>{hasScheduledCalendarFields(item) ? (item.allDay ? "All" : formatScheduledItemTime(item, timezone).split(" - ")[0]) : formatTaskDueTime(item, timezone)}</div>
                                                            <SmallText>Calendar Item</SmallText>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: 700 }}>{item.title}</div>
                                                            <SmallText>{hasScheduledCalendarFields(item) ? getCalendarItemSourceLabel(item.source, "Calendar Item") : getItemTypeLabel(item.itemTypeUuid || item.taskType, managedItemTypes)}</SmallText>
                                                        </div>
                                                    </TodayItem>
                                                ))}
                                                <StatBox>
                                                    <SectionLabel>Week Overview</SectionLabel>
                                                    <div style={{ display: "grid", gap: 8 }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Meeting hours this week</SmallText><span style={{ color: "#f8c860", fontWeight: 700 }}>{weekStats.meetingHours}h</span></div>
                                                        <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Focus time blocked</SmallText><span style={{ color: "#5fd4a8", fontWeight: 700 }}>{weekStats.focusHours}h</span></div>
                                                        <div style={{ display: "flex", justifyContent: "space-between" }}><SmallText>Tasks completed</SmallText><span style={{ fontWeight: 700 }}>{weekStats.completed} / {weekStats.total}</span></div>
                                                    </div>
                                                </StatBox>
                                            </>
                                        )}
                                    </RightPanelInner>
                                </>
                            )}
                        </>
                    )}
                </RightPanel>
            </Layout>

            {showCalendarItemModal && (
                <StandardModal
                    isDarkMode={themeMode === "dark"}
                    open={showCalendarItemModal}
                    onClose={closeCalendarItemModal}
                    maxWidth="md"
                    bodyPadding="0"
                    hideCloseButton
                >
                    <CalendarItemModal
                        themeMode={themeMode}
                        user={user}
                        item={editingCalendarItem}
                        prefillTask={taskPrefill}
                        hideSmartSuggestion={Boolean(taskPrefill && !editingCalendarItem)}
                        selectedDate={selectedDate}
                        visibleScheduledItems={visibleScheduledItems}
                        visibleTasks={visibleTasks}
                        itemTypes={managedItemTypes}
                        locationInfo={locationInfo}
                        timezone={timezone}
                        timezoneAttendees={timezoneAttendees}
                        onClose={closeCalendarItemModal}
                        onSave={handleSaveCalendarItem}
                        onDelete={handleDeleteCalendarItem}
                    />
                </StandardModal>
            )}

            {showItemTypeManager && (
                <StandardModal isDarkMode={themeMode === "dark"} open={showItemTypeManager} onClose={() => setShowItemTypeManager(false)} maxWidth="md" bodyPadding="0" hideCloseButton>
                    <ItemTypeManagerModal
                        themeMode={themeMode}
                        itemTypes={managedItemTypes}
                        customItemTypes={customItemTypes}
                        tasks={tasks}
                        canManageCompanyItemTypes={canManageSharedCalendar}
                        onCreate={handleCreateItemType}
                        onUpdate={handleUpdateItemType}
                        onDelete={handleDeleteItemType}
                        onClose={() => setShowItemTypeManager(false)}
                    />
                </StandardModal>
            )}

            {showDetailModal && detailItem && (
                <StandardModal isDarkMode={themeMode === "dark"} open={showDetailModal} onClose={() => setShowDetailModal(false)} maxWidth="md" bodyPadding="0" hideCloseButton>
                    <DetailModal
                        themeMode={themeMode}
                        item={detailItem}
                        timezone={timezone}
                        itemTypes={managedItemTypes}
                        user={user}
                        companyUsers={companyUsers}
                        onClose={() => setShowDetailModal(false)}
                        onEdit={openItemEdit}
                        onViewFeature={openAutomationFeature}
                    />
                </StandardModal>
            )}
        </Surface>
    );
}
